import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faSpinner, faDownload, faSearch,
  faQrcode, faSync, faPlay, faStop, faPaperPlane, faTriangleExclamation,
  faWallet,
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { apiJson } from '../apiClient';
import { PageHeader } from '../../crm/components/ui/PageHeader';
import { Panel } from '../../crm/components/ui/Panel';
import { KpiCard, KpiGrid } from '../../crm/components/ui/KpiCard';
import { Badge, StatusBadge } from '../../crm/components/ui/Badge';
import { Button } from '../../crm/components/ui/Button';
import { Tabs } from '../../crm/components/ui/Tabs';
import { Sheet } from '../../crm/components/ui/Sheet';
import { EmptyState } from '../../crm/components/ui/EmptyState';
import { SegmentedControl } from '../../crm/components/ui/SegmentedControl';
import { AssinaturaSheet } from '../../crm/components/financeiro/AssinaturaSheet';
import { CobrancaWizardSheet } from '../../crm/components/financeiro/CobrancaWizardSheet';
import { EntityLink } from '../../crm/components/ui/EntityLink';
import { RowMenu } from '../../crm/components/ui/RowMenu';
import { LancamentoSheet } from '../../crm/components/financeiro/LancamentoSheet';
import { BulkActionBar } from '../../crm/components/ui/BulkActionBar';
import { VincularProjetoSelector } from '../../crm/components/financeiro/VincularProjetoSelector';
import { RegistrarPagamentoDialog } from '../../crm/components/financeiro/RegistrarPagamentoDialog';
import { MonthPicker } from '../../crm/components/ui/MonthPicker';
import { PeriodFilterBar } from '../../crm/components/financeiro/PeriodFilterBar';
import {
  ResponsiveContainer, AreaChart, Area, ComposedChart, Bar, Line, BarChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from 'recharts';

// Cores fixas em hex (não var() do tema) porque recharts precisa dos
// valores no primeiro render do SVG — tunadas pro tema claro (padrão);
// se o usuário alternar pro escuro os gráficos continuam legíveis (a
// paleta funciona nos dois fundos), só não re-otimizam automaticamente.
const CORES = { accent: '#6366f1', positive: '#15803d', danger: '#dc2626', warning: '#b45309', grid: 'rgba(15,15,20,0.08)', texto: '#71717a' };

const apiFin = (path, opts) => apiJson(`/financeiro${path}`, opts);
const apiClientesFn = (path, opts) => apiJson(`/clientes${path}`, opts);

const fmtBRL = (v) => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return typeof n === 'number' && !isNaN(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
};
const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '—');
const hoje = () => new Date().toISOString().slice(0, 10);
const mesAtual = () => new Date().toISOString().slice(0, 7);
const mesAnterior = (competencia) => {
  const [a, m] = competencia.split('-').map(Number);
  const d = new Date(a, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const nomeCompetencia = (c) => {
  const [a, m] = c.split('-').map(Number);
  return new Date(a, m - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
};
const mesCurto = (c) => {
  const [a, m] = c.split('-').map(Number);
  const s = new Date(a, m - 1, 1).toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`);
const fmtNum = (v) => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return typeof n === 'number' && !isNaN(n) ? n : 0;
};

function exportarCSV(nomeArquivo, linhas) {
  const escapar = (v) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const csv = linhas.map((l) => l.map(escapar).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = nomeArquivo;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const TABS = [
  { id: 'geral', label: 'Visão Geral' },
  { id: 'receitas', label: 'Receitas' },
  { id: 'assinaturas', label: 'Assinaturas' },
  { id: 'cobrancas', label: 'Cobranças' },
  { id: 'despesas', label: 'Despesas' },
  { id: 'folha', label: 'Folha' },
  { id: 'relatorios', label: 'Relatórios' },
  { id: 'whatsapp', label: 'WhatsApp' },
];

// ─── Visão Geral (§16) ───────────────────────────────────────────────────
function RowAtencao({ label, onClick }) {
  return (
    <div className="clickable" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }} onClick={onClick}>
      <span>{label}</span>
      <span className="crm-form-hint">›</span>
    </div>
  );
}

// ─── Visão Geral (dashboard executivo — Financeiro → Visão Geral) ───────
function AbaVisaoGeral({ showToast, onNavegar, onIrParaReceitas, onIrParaCobrancas, onIrParaAssinaturas, onIrParaDespesas, onIrParaAba }) {
  const [de, setDe] = useState(mesAtual());
  const [ate, setAte] = useState(mesAtual());
  const [clienteId, setClienteId] = useState('');
  const [projetoId, setProjetoId] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [clientes, setClientes] = useState([]);
  const [projetos, setProjetos] = useState([]);
  const [dash, setDash] = useState(null);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [assinaturaAberta, setAssinaturaAberta] = useState(null);
  const [mrrDrawer, setMrrDrawer] = useState(null);
  const [mrrDrawerDados, setMrrDrawerDados] = useState(null);

  useEffect(() => { apiClientesFn('').then((r) => setClientes(r.lista || [])); }, []);

  // Projeto reage ao Cliente (Seção 3 do pedido) — se um cliente está
  // selecionado, só mostra projetos associados a ele; troca de cliente
  // limpa uma seleção de projeto que não pertença mais a ele.
  useEffect(() => {
    const params = new URLSearchParams();
    if (clienteId) params.set('cliente_id', clienteId);
    apiJson(`/projetos?${params}`).then((r) => {
      if (r.status !== 'ok') return;
      setProjetos(r.lista || []);
      if (projetoId && !(r.lista || []).some((p) => p.id === projetoId)) setProjetoId('');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  useEffect(() => {
    const params = new URLSearchParams({ de, ate });
    if (clienteId) params.set('cliente_id', clienteId);
    if (projetoId) params.set('projeto_id', projetoId);
    if (tipoFiltro) params.set('tipo', tipoFiltro);
    apiFin(`/dashboard/executivo?${params}`).then((r) => { if (r.status === 'ok') setDash(r.dashboard); });
  }, [de, ate, clienteId, projetoId, tipoFiltro]);

  const abrirMrrDrawer = (competencia) => {
    setMrrDrawer(competencia);
    setMrrDrawerDados(null);
    const params = new URLSearchParams({ competencia });
    if (clienteId) params.set('cliente_id', clienteId);
    if (projetoId) params.set('projeto_id', projetoId);
    apiFin(`/dashboard/mrr-detalhe?${params}`).then((r) => { if (r.status === 'ok') setMrrDrawerDados(r.detalhe); });
  };

  if (!dash) return <div className="crm-form-hint"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>;

  const {
    kpis, mrr_evolucao, mrr_crescimento, resultado_mensal, contas_a_receber, aging,
    maiores_devedores, proximos_recebimentos, receita_recorrente_projetada,
    composicao_receita, top_clientes, concentracao, inadimplencia,
    despesas_por_categoria, resultado_acumulado, requer_atencao, cobrancas_automaticas,
  } = dash;

  const periodoLabel = de === ate ? nomeCompetencia(de) : `${mesCurto(de)}/${de.slice(0, 4)} – ${mesCurto(ate)}/${ate.slice(0, 4)}`;
  const dadosMrr = mrr_evolucao.map((m) => ({ ...m, mesLabel: mesCurto(m.competencia), mrrNum: fmtNum(m.mrr), novasNum: fmtNum(m.novas), canceladasNum: fmtNum(m.canceladas) }));
  const dadosResultado = resultado_mensal.map((m) => ({ ...m, mesLabel: mesCurto(m.competencia), receitaNum: fmtNum(m.receita), despesaNum: fmtNum(m.despesa), resultadoNum: fmtNum(m.resultado) }));
  const dadosTopClientes = top_clientes.map((c) => ({ ...c, valorNum: fmtNum(c.valor) }));
  const dadosDespesasCategoria = despesas_por_categoria.map((c) => ({ ...c, valorNum: fmtNum(c.valor) }));
  const nadaRequerAtencao = requer_atencao.clientes_em_atraso === 0 && requer_atencao.cobrancas_vencidas === 0 && requer_atencao.automacoes_pausadas === 0 && requer_atencao.automacoes_com_erro === 0 && !requer_atencao.whatsapp_desconectado && requer_atencao.despesas_vencendo_semana === 0;

  // Faixa de dias -> intervalo de vencimento real, pra aging ser clicável
  // de verdade (Seção 10 do pedido) e não só levar pra "todos os vencidos".
  const intervaloDaFaixa = (f) => {
    const hoje = new Date();
    const ateData = new Date(hoje); ateData.setDate(ateData.getDate() - f.dias_min);
    const deData = f.dias_max === null ? new Date(2000, 0, 1) : new Date(hoje.getTime() - f.dias_max * 86400000);
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { vencimentoDe: fmt(deData), vencimentoAte: fmt(ateData) };
  };

  const TooltipMRR = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    const idx = mrr_evolucao.findIndex((x) => x.competencia === p.competencia);
    const anteriorVal = idx > 0 ? fmtNum(mrr_evolucao[idx - 1].mrr) : null;
    const variacaoPct = anteriorVal ? ((p.mrrNum - anteriorVal) / anteriorVal) * 100 : null;
    const variacaoLiquida = p.novasNum - p.canceladasNum;
    return (
      <div className="crm-panel" style={{ padding: 'var(--crm-space-3)', minWidth: 180 }}>
        <div style={{ fontWeight: 600, textTransform: 'capitalize', marginBottom: 4 }}>{nomeCompetencia(p.competencia)}</div>
        <div>MRR: {fmtBRL(p.mrr)}</div>
        {p.novasNum > 0 && <div style={{ color: CORES.positive }}>Novas assinaturas: +{fmtBRL(p.novasNum)}</div>}
        {p.canceladasNum > 0 && <div style={{ color: CORES.danger }}>Cancelamentos: -{fmtBRL(p.canceladasNum)}</div>}
        {(p.novasNum > 0 || p.canceladasNum > 0) && <div className="crm-form-hint">Variação líquida: {variacaoLiquida >= 0 ? '+' : ''}{fmtBRL(variacaoLiquida)}</div>}
        {variacaoPct !== null && <div className="crm-form-hint">Variação: {fmtPct(variacaoPct)}</div>}
        <div className="crm-form-hint" style={{ marginTop: 4 }}>Clique para ver as assinaturas do mês</div>
      </div>
    );
  };

  const TooltipResultado = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
      <div className="crm-panel" style={{ padding: 'var(--crm-space-3)', minWidth: 180 }}>
        <div style={{ fontWeight: 600, textTransform: 'capitalize', marginBottom: 4 }}>{nomeCompetencia(p.competencia)}</div>
        <div>Receita: {fmtBRL(p.receita)}</div>
        <div>Despesa: {fmtBRL(p.despesa)}</div>
        <div style={{ color: p.resultadoNum < 0 ? CORES.danger : CORES.positive }}>Resultado: {fmtBRL(p.resultado)}</div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-4)', flexWrap: 'wrap' }}>
        <PeriodFilterBar onChange={(novoDe, novoAte) => { setDe(novoDe); setAte(novoAte); }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)' }}>
          <span className="crm-form-hint">Cliente:</span>
          <select className="crm-select" value={clienteId} onChange={(e) => setClienteId(e.target.value)} style={{ maxWidth: '180px' }}>
            <option value="">Todos</option>
            {clientes.map((c) => <option key={c._id} value={c._id}>{c.nome}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)' }}>
          <span className="crm-form-hint">Projeto:</span>
          <select className="crm-select" value={projetoId} onChange={(e) => setProjetoId(e.target.value)} style={{ maxWidth: '180px' }}>
            <option value="">Todos</option>
            {projetos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)' }}>
          <span className="crm-form-hint">Tipo:</span>
          <select className="crm-select" value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)} style={{ maxWidth: '140px' }}>
            <option value="">Todos</option>
            <option value="ASSINATURA">Recorrente</option>
            <option value="AVULSO">Avulsa</option>
          </select>
        </label>
      </div>

      <KpiGrid>
        <div className="clickable" onClick={() => onIrParaAssinaturas({ status: 'ATIVA', competencia: ate })} title="Ver assinaturas ativas">
          <KpiCard label="MRR atual" value={fmtBRL(kpis.mrr_atual)} />
          {kpis.mrr_variacao_pct !== null && <div className="crm-form-hint" style={{ marginTop: 2 }}>{fmtPct(kpis.mrr_variacao_pct)} no período</div>}
        </div>
        <div className="clickable" onClick={() => onIrParaReceitas({ de, ate, status: 'PENDENTE', clienteId })} title="Ver contas a receber pendentes">
          <KpiCard label="A receber" value={fmtBRL(kpis.a_receber)} />
        </div>
        <div className="clickable" onClick={() => onIrParaReceitas({ de, ate, status: 'VENCIDO', clienteId })} title="Ver contas vencidas">
          <KpiCard label="Vencido" value={fmtBRL(kpis.vencido)} tone={fmtNum(kpis.vencido) > 0 ? 'danger' : undefined} />
          <div className="crm-form-hint" style={{ marginTop: 2 }}>{kpis.vencido_clientes} cliente(s)</div>
        </div>
        <div className="clickable" onClick={() => onIrParaAba('relatorios')} title="Ver relatório do período">
          <KpiCard label="Resultado" value={fmtBRL(kpis.resultado)} tone={fmtNum(kpis.resultado) < 0 ? 'danger' : undefined} />
        </div>
      </KpiGrid>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 'var(--crm-space-4)' }}>
        <Panel title={`Evolução do MRR — ${periodoLabel}`} flush>
          <div style={{ padding: 'var(--crm-space-4)' }}>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={dadosMrr} onClick={(e) => { const p = e?.activePayload?.[0]?.payload; if (p) abrirMrrDrawer(p.competencia); }}>
                <defs>
                  <linearGradient id="gradMrr" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CORES.accent} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={CORES.accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={CORES.grid} vertical={false} />
                <XAxis dataKey="mesLabel" stroke={CORES.texto} fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke={CORES.texto} fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} width={40} />
                <Tooltip content={<TooltipMRR />} cursor={{ stroke: CORES.accent, strokeWidth: 1 }} />
                <Area type="monotone" dataKey="mrrNum" stroke={CORES.accent} strokeWidth={2} fill="url(#gradMrr)" style={{ cursor: 'pointer' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: 'flex', gap: 'var(--crm-space-4)', padding: '0 var(--crm-space-4) var(--crm-space-4)', flexWrap: 'wrap' }}>
            <div><div className="crm-form-hint">MRR início do período</div><div style={{ fontWeight: 600 }}>{fmtBRL(mrr_crescimento.mrr_inicio_periodo)}</div></div>
            <div><div className="crm-form-hint">MRR atual</div><div style={{ fontWeight: 600 }}>{fmtBRL(mrr_crescimento.mrr_atual)}</div></div>
            <div><div className="crm-form-hint">Crescimento no período</div><div style={{ fontWeight: 600, color: fmtNum(mrr_crescimento.crescimento_absoluto) < 0 ? CORES.danger : CORES.positive }}>{fmtPct(mrr_crescimento.crescimento_pct)}</div></div>
            <div><div className="crm-form-hint">Variação absoluta</div><div style={{ fontWeight: 600 }}>{fmtBRL(mrr_crescimento.crescimento_absoluto)}</div></div>
          </div>
        </Panel>

        <Panel title="Próximos recebimentos" flush>
          <div style={{ padding: 'var(--crm-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="crm-form-hint">Próximos 7 dias</span><strong>{fmtBRL(proximos_recebimentos['7_dias'])}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="crm-form-hint">Próximos 30 dias</span><strong>{fmtBRL(proximos_recebimentos['30_dias'])}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="crm-form-hint">Próximos 90 dias</span><strong>{fmtBRL(proximos_recebimentos['90_dias'])}</strong></div>
          </div>
          {proximos_recebimentos.lista.length === 0 ? <EmptyState title="Nada previsto" /> : (
            <div className="crm-table-wrap">
              <table className="crm-table">
                <tbody>
                  {proximos_recebimentos.lista.map((l) => (
                    <tr key={l.receivable_id} className="clickable" onClick={() => setItemSelecionado({ id: l.receivable_id, _origem: 'receivable' })}>
                      <td>{fmtDate(l.vencimento)}</td>
                      <td className="crm-table-primary">{l.cliente_nome || '—'}</td>
                      <td>{l.projeto_nome || l.descricao}</td>
                      <td>{fmtBRL(l.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 'var(--crm-space-4)' }}>
        <Panel title={`Resultado financeiro — ${periodoLabel}`} flush>
          <div style={{ padding: 'var(--crm-space-4)' }}>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={dadosResultado}>
                <CartesianGrid strokeDasharray="3 3" stroke={CORES.grid} vertical={false} />
                <XAxis dataKey="mesLabel" stroke={CORES.texto} fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke={CORES.texto} fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} width={40} />
                <Tooltip content={<TooltipResultado />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                <Bar dataKey="receitaNum" name="Receita" fill={CORES.positive} radius={[4, 4, 0, 0]} onClick={(d) => onIrParaReceitas({ competencia: d.payload.competencia, clienteId })} style={{ cursor: 'pointer' }} />
                <Bar dataKey="despesaNum" name="Despesa" fill={CORES.danger} radius={[4, 4, 0, 0]} onClick={(d) => onIrParaDespesas({ competencia: d.payload.competencia })} style={{ cursor: 'pointer' }} />
                <Line type="monotone" dataKey="resultadoNum" name="Resultado" stroke={CORES.accent} strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-4)' }}>
          <Panel title="Contas a receber" flush>
            <div style={{ padding: 'var(--crm-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="crm-form-hint">Recebido no período</span><strong>{fmtBRL(contas_a_receber.recebido)}</strong></div>
              <div className="clickable" style={{ display: 'flex', justifyContent: 'space-between' }} onClick={() => onIrParaReceitas({ de, ate, status: 'PENDENTE', clienteId })}><span className="crm-form-hint">A vencer</span><strong>{fmtBRL(contas_a_receber.a_vencer)}</strong></div>
              <div className="clickable" style={{ display: 'flex', justifyContent: 'space-between' }} onClick={() => onIrParaReceitas({ de, ate, status: 'VENCIDO', clienteId })}><span className="crm-form-hint" style={{ color: CORES.danger }}>Vencido</span><strong style={{ color: CORES.danger }}>{fmtBRL(contas_a_receber.vencido)}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="crm-form-hint">Próximos 7 dias</span><strong>{fmtBRL(proximos_recebimentos['7_dias'])}</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span className="crm-form-hint">Próximos 30 dias</span><strong>{fmtBRL(proximos_recebimentos['30_dias'])}</strong></div>
            </div>
          </Panel>
          <Panel title="Contas vencidas — por faixa de atraso" flush>
            <div style={{ padding: 'var(--crm-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-2)' }}>
              {aging.map((f) => (
                <div key={f.faixa} className="clickable" style={{ display: 'flex', justifyContent: 'space-between' }} onClick={() => onIrParaReceitas({ de, ate, status: 'VENCIDO', clienteId, ...intervaloDaFaixa(f) })}>
                  <span className="crm-form-hint">{f.faixa} dias</span>
                  <strong>{fmtBRL(f.valor)}</strong>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 'var(--crm-space-4)' }}>
        <Panel title={`Receita por cliente — ${periodoLabel}`} flush>
          {dadosTopClientes.length === 0 ? <EmptyState title="Sem receita neste período" /> : (
            <div style={{ padding: 'var(--crm-space-4)' }}>
              <ResponsiveContainer width="100%" height={Math.max(180, dadosTopClientes.length * 36)}>
                <BarChart data={dadosTopClientes} layout="vertical" margin={{ left: 24 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="cliente_nome" width={120} stroke={CORES.texto} fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => fmtBRL(v)} cursor={{ fill: 'rgba(255,255,255,0.04)' }} contentStyle={{ background: 'var(--crm-bg)', border: '1px solid var(--crm-border)' }} />
                  <Bar dataKey="valorNum" fill={CORES.accent} radius={[0, 4, 4, 0]} onClick={(d) => onNavegar('clientes', { clienteId: d.payload.cliente_id })} style={{ cursor: 'pointer' }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {concentracao.maior_cliente_nome && (
            <div style={{ padding: '0 var(--crm-space-4) var(--crm-space-4)', display: 'flex', gap: 'var(--crm-space-4)', flexWrap: 'wrap' }}>
              <div><div className="crm-form-hint">Maior cliente</div><div style={{ fontWeight: 600 }}>{concentracao.maior_cliente_nome} — {concentracao.maior_cliente_pct.toFixed(0)}%</div></div>
              <div><div className="crm-form-hint">Top 3 clientes</div><div style={{ fontWeight: 600 }}>{concentracao.top3_pct.toFixed(0)}% da receita</div></div>
            </div>
          )}
        </Panel>

        <Panel title={`Composição da receita — ${periodoLabel}`} flush>
          <div style={{ padding: 'var(--crm-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-3)' }}>
            <div className="clickable" onClick={() => onIrParaReceitas({ de, ate, tipo: 'ASSINATURA', clienteId })}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--crm-text-sm)' }}><span>Recorrente</span><span>{fmtBRL(composicao_receita.recorrente)}{composicao_receita.recorrente_pct !== null && ` (${composicao_receita.recorrente_pct.toFixed(0)}%)`}</span></div>
              <div style={{ height: 8, borderRadius: 4, background: 'var(--crm-border)', marginTop: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${composicao_receita.recorrente_pct || 0}%`, background: CORES.accent }} />
              </div>
            </div>
            <div className="clickable" onClick={() => onIrParaReceitas({ de, ate, tipo: 'AVULSO', clienteId })}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--crm-text-sm)' }}><span>Avulsa</span><span>{fmtBRL(composicao_receita.avulsa)}{composicao_receita.avulsa_pct !== null && ` (${composicao_receita.avulsa_pct.toFixed(0)}%)`}</span></div>
              <div style={{ height: 8, borderRadius: 4, background: 'var(--crm-border)', marginTop: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${composicao_receita.avulsa_pct || 0}%`, background: CORES.warning }} />
              </div>
            </div>
          </div>
          <div style={{ padding: '0 var(--crm-space-4) var(--crm-space-4)' }}>
            <div className="crm-form-hint" style={{ marginBottom: 'var(--crm-space-2)' }}>Receita recorrente contratada — valores previstos pelas assinaturas ativas (não é previsão por IA).</div>
            {receita_recorrente_projetada.map((m) => (
              <div key={m.competencia} className="clickable" style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }} onClick={() => abrirMrrDrawer(m.competencia)}>
                <span style={{ textTransform: 'capitalize' }}>{nomeCompetencia(m.competencia)}</span>
                <strong>{fmtBRL(m.valor)}</strong>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 'var(--crm-space-4)' }}>
        <Panel title="Maiores pendências" flush>
          {maiores_devedores.length === 0 ? <EmptyState title="Nenhum cliente com valores vencidos" /> : (
            <div className="crm-table-wrap">
              <table className="crm-table">
                <thead><tr><th>Cliente</th><th>Valor</th><th>Cobranças</th><th>Atraso mais antigo</th></tr></thead>
                <tbody>
                  {maiores_devedores.map((d) => (
                    <tr key={d.cliente_id}>
                      <td className="crm-table-primary"><EntityLink onClick={() => onNavegar('clientes', { clienteId: d.cliente_id })}>{d.cliente_nome}</EntityLink></td>
                      <td className="clickable" onClick={() => onIrParaReceitas({ de, ate, status: 'VENCIDO', clienteId: d.cliente_id })}>{fmtBRL(d.valor)}</td>
                      <td>{d.qtd_cobrancas}</td>
                      <td>{d.atraso_mais_antigo_dias} dias</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="Inadimplência" flush>
          <div style={{ padding: 'var(--crm-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-2)' }} title={inadimplencia.formula}>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: (inadimplencia.taxa_pct || 0) > 10 ? CORES.danger : undefined }}>{inadimplencia.taxa_pct !== null ? `${inadimplencia.taxa_pct.toFixed(1)}%` : '—'}</div>
            <div className="crm-form-hint">{inadimplencia.formula}</div>
            <div style={{ display: 'flex', gap: 'var(--crm-space-4)', marginTop: 'var(--crm-space-2)' }}>
              <div><div className="crm-form-hint">Clientes em atraso</div><div style={{ fontWeight: 600 }}>{inadimplencia.clientes_em_atraso}</div></div>
              <div><div className="crm-form-hint">Valor vencido</div><div style={{ fontWeight: 600 }}>{fmtBRL(inadimplencia.valor_vencido)}</div></div>
            </div>
          </div>
        </Panel>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 'var(--crm-space-4)' }}>
        <Panel title={`Despesas por categoria — ${periodoLabel}`} flush>
          {dadosDespesasCategoria.length === 0 ? <EmptyState title="Nenhuma despesa paga neste período" /> : (
            <div style={{ padding: 'var(--crm-space-4)' }}>
              <ResponsiveContainer width="100%" height={Math.max(160, dadosDespesasCategoria.length * 36)}>
                <BarChart data={dadosDespesasCategoria} layout="vertical" margin={{ left: 24 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="categoria" width={130} stroke={CORES.texto} fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => fmtBRL(v)} cursor={{ fill: 'rgba(255,255,255,0.04)' }} contentStyle={{ background: 'var(--crm-bg)', border: '1px solid var(--crm-border)' }} />
                  <Bar dataKey="valorNum" fill={CORES.danger} radius={[0, 4, 4, 0]} onClick={(d) => onIrParaDespesas({ de, ate, categoria: d.payload.categoria === 'Sem categoria' ? '' : d.payload.categoria })} style={{ cursor: 'pointer' }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        <Panel title="Requer atenção" flush>
          <div style={{ padding: 'var(--crm-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-2)' }}>
            {requer_atencao.clientes_em_atraso > 0 && <RowAtencao label={`${requer_atencao.clientes_em_atraso} cliente(s) com pagamentos vencidos`} onClick={() => onIrParaReceitas({ de, ate, status: 'VENCIDO', clienteId })} />}
            {requer_atencao.cobrancas_vencidas > 0 && <RowAtencao label={`${requer_atencao.cobrancas_vencidas} cobrança(s) vencida(s)`} onClick={() => onIrParaReceitas({ de, ate, status: 'VENCIDO', clienteId })} />}
            {requer_atencao.automacoes_pausadas > 0 && <RowAtencao label={`${requer_atencao.automacoes_pausadas} automação(ões) pausada(s)`} onClick={() => onIrParaCobrancas('PAUSADA')} />}
            {requer_atencao.automacoes_com_erro > 0 && <RowAtencao label={`${requer_atencao.automacoes_com_erro} cobrança(s) automática(s) com erro`} onClick={() => onIrParaAba('cobrancas')} />}
            {requer_atencao.whatsapp_desconectado && <RowAtencao label="WhatsApp desconectado" onClick={() => onIrParaAba('whatsapp')} />}
            {requer_atencao.despesas_vencendo_semana > 0 && <RowAtencao label={`${requer_atencao.despesas_vencendo_semana} despesa(s) vencendo nos próximos 7 dias`} onClick={() => onIrParaAba('despesas')} />}
            {nadaRequerAtencao && <div className="crm-form-hint">Nada pendente no momento.</div>}
          </div>
          <div style={{ borderTop: '1px solid var(--crm-border)', padding: 'var(--crm-space-4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--crm-space-2)' }}>
            <div style={{ display: 'flex', gap: 'var(--crm-space-4)', fontSize: 'var(--crm-text-sm)' }}>
              <span>Ativas: <strong>{cobrancas_automaticas.ativas}</strong></span>
              <span>Hoje: <strong>{cobrancas_automaticas.proximas_hoje}</strong></span>
              <span style={{ color: cobrancas_automaticas.falharam > 0 ? CORES.danger : undefined }}>Falharam: <strong>{cobrancas_automaticas.falharam}</strong></span>
              <span>Pausadas: <strong>{cobrancas_automaticas.pausadas}</strong></span>
            </div>
            <Button size="sm" variant="outline" onClick={() => onIrParaAba('cobrancas')}>Ver agenda</Button>
          </div>
        </Panel>
      </div>

      <div>
        <div className="crm-form-hint" style={{ marginBottom: 'var(--crm-space-2)' }}>Resultado acumulado — {periodoLabel}</div>
        <KpiGrid>
          <KpiCard label="Receita recebida" value={fmtBRL(resultado_acumulado.receita_recebida)} />
          <KpiCard label="Despesas pagas" value={fmtBRL(resultado_acumulado.despesas_pagas)} />
          <KpiCard label="Resultado" value={fmtBRL(resultado_acumulado.resultado)} tone={fmtNum(resultado_acumulado.resultado) < 0 ? 'danger' : undefined} />
        </KpiGrid>
      </div>

      <Sheet
        open={!!mrrDrawer}
        onClose={() => setMrrDrawer(null)}
        title={mrrDrawer ? `MRR — ${nomeCompetencia(mrrDrawer)}` : ''}
        subtitle={mrrDrawerDados ? `Total: ${fmtBRL(mrrDrawerDados.total)}` : ''}
      >
        {!mrrDrawerDados ? (
          <div className="crm-form-hint"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>
        ) : mrrDrawerDados.assinaturas.length === 0 ? (
          <EmptyState title="Nenhuma assinatura ativa neste mês" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-3)' }}>
            {mrrDrawerDados.assinaturas.map((a) => (
              <div key={a.rule_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--crm-border)', paddingBottom: 'var(--crm-space-2)' }}>
                <div>
                  <div className="crm-table-primary">{a.descricao}</div>
                  <div className="crm-form-hint">
                    {a.cliente_id ? <EntityLink onClick={() => onNavegar('clientes', { clienteId: a.cliente_id })}>{a.cliente_nome}</EntityLink> : '—'}
                    {a.projeto_id && <> · <EntityLink onClick={() => onNavegar('projetos', { projetoId: a.projeto_id })}>{a.projeto_nome}</EntityLink></>}
                  </div>
                </div>
                <strong>{fmtBRL(a.valor)}</strong>
              </div>
            ))}
          </div>
        )}
      </Sheet>

      {itemSelecionado && (
        <LancamentoSheet
          item={itemSelecionado}
          onClose={() => setItemSelecionado(null)}
          onChanged={() => {}}
          showToast={showToast}
          onNavegar={onNavegar}
          onAbrirAssinatura={setAssinaturaAberta}
        />
      )}
      <AssinaturaSheet assinaturaId={assinaturaAberta} open={!!assinaturaAberta} onClose={() => setAssinaturaAberta(null)} onChanged={() => {}} showToast={showToast} onNavegar={onNavegar} />
    </div>
  );
}

// ─── Sheet de criação de lançamento (receita/despesa) ───────────────────
function LancamentoCreateSheet({ tipo, clientes, clienteIdInicial, onClose, onCreated, showToast }) {
  const [form, setForm] = useState({ descricao: '', valor: '', vencimento: hoje(), cliente_id: clienteIdInicial || '', categoria: '', fornecedor_nome: '', forma_pagamento_prevista: '' });
  const [salvando, setSalvando] = useState(false);
  const [sugestao, setSugestao] = useState(null);
  const set = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });

  // Item 6 do diagnóstico de usabilidade: sugere categoria/forma de
  // pagamento a partir do último lançamento do mesmo cliente (receita) ou
  // fornecedor (despesa) — nunca sobrescreve o que a pessoa já digitou.
  const sugerirApartirDe = (lista) => {
    if (!lista || lista.length === 0) { setSugestao(null); return; }
    const ultimo = lista[0];
    const sug = { categoria: ultimo.categoria || '', forma_pagamento_prevista: ultimo.forma_pagamento_prevista || '' };
    if (!sug.categoria && !sug.forma_pagamento_prevista) { setSugestao(null); return; }
    setSugestao(sug);
    setForm((f) => ({
      ...f,
      categoria: f.categoria || sug.categoria,
      forma_pagamento_prevista: f.forma_pagamento_prevista || sug.forma_pagamento_prevista,
    }));
  };

  useEffect(() => {
    if (tipo !== 'RECEITA' || !form.cliente_id) { setSugestao(null); return; }
    apiFin(`/receivables?cliente_id=${form.cliente_id}`).then((r) => sugerirApartirDe(r.status === 'ok' ? r.lista : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, form.cliente_id]);

  const buscarSugestaoFornecedor = async () => {
    if (tipo === 'RECEITA' || !form.fornecedor_nome.trim()) return;
    const r = await apiFin(`/payables?busca=${encodeURIComponent(form.fornecedor_nome.trim())}`);
    if (r.status === 'ok') {
      const doMesmoFornecedor = r.lista.filter((p) => (p.fornecedor_nome || '').toLowerCase() === form.fornecedor_nome.trim().toLowerCase());
      sugerirApartirDe(doMesmoFornecedor);
    }
  };

  const salvar = async () => {
    if (!form.descricao.trim() || !form.valor || !form.vencimento) { showToast('Preencha descrição, valor e vencimento', 'error'); return; }
    setSalvando(true);
    const competencia = form.vencimento.slice(0, 7);
    let r;
    if (tipo === 'RECEITA') {
      r = await apiFin('/receivables', { method: 'POST', body: JSON.stringify({ tipo: 'AVULSO', descricao: form.descricao, categoria: form.categoria || undefined, competencia, valor: form.valor, vencimento: form.vencimento, cliente_id: form.cliente_id || undefined, forma_pagamento_prevista: form.forma_pagamento_prevista || undefined }) });
    } else {
      r = await apiFin('/payables', { method: 'POST', body: JSON.stringify({ tipo: 'DESPESA', descricao: form.descricao, categoria: form.categoria || undefined, competencia, valor_base: form.valor, vencimento: form.vencimento, fornecedor_nome: form.fornecedor_nome || undefined, forma_pagamento_prevista: form.forma_pagamento_prevista || undefined }) });
    }
    setSalvando(false);
    if (r.status === 'ok' || r.receivable || r.payable) { showToast(`${tipo === 'RECEITA' ? 'Receita' : 'Despesa'} criada!`, 'success'); onCreated(); onClose(); }
    else showToast(r.mensagem || 'Erro ao criar', 'error');
  };

  return (
    <Sheet open onClose={onClose} title={`Nova ${tipo === 'RECEITA' ? 'Receita' : 'Despesa'}`} footer={<Button variant="primary" loading={salvando} onClick={salvar}>Salvar</Button>}>
      <div className="crm-form-field"><label className="crm-form-label">Descrição</label><input className="crm-input" value={form.descricao} onChange={set('descricao')} /></div>
      {tipo === 'RECEITA' ? (
        <div className="crm-form-field">
          <label className="crm-form-label">Cliente (opcional)</label>
          <select className="crm-select" value={form.cliente_id} onChange={set('cliente_id')}>
            <option value="">—</option>
            {clientes.map((c) => <option key={c._id} value={c._id}>{c.nome}</option>)}
          </select>
        </div>
      ) : (
        <div className="crm-form-field"><label className="crm-form-label">Fornecedor (opcional)</label><input className="crm-input" value={form.fornecedor_nome} onChange={set('fornecedor_nome')} onBlur={buscarSugestaoFornecedor} /></div>
      )}
      <div className="crm-form-field"><label className="crm-form-label">Valor</label><input className="crm-input" type="number" step="0.01" value={form.valor} onChange={set('valor')} /></div>
      <div className="crm-form-field"><label className="crm-form-label">Categoria (opcional)</label><input className="crm-input" value={form.categoria} onChange={set('categoria')} /></div>
      <div className="crm-form-field"><label className="crm-form-label">Forma de pagamento (opcional)</label><input className="crm-input" value={form.forma_pagamento_prevista} onChange={set('forma_pagamento_prevista')} /></div>
      <div className="crm-form-field"><label className="crm-form-label">Vencimento</label><input className="crm-input" type="date" value={form.vencimento} onChange={set('vencimento')} /></div>
      {sugestao && <div className="crm-form-hint">Categoria/forma de pagamento sugeridas com base no último lançamento — pode alterar à vontade.</div>}
    </Sheet>
  );
}

// ─── Receitas avulsas (§21) ──────────────────────────────────────────────
function AbaReceitas({ showToast, clienteIdContexto, abrirCriacao, onNavegar, onAbrirAssinatura, filtroInicial }) {
  const [linhas, setLinhas] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [projetos, setProjetos] = useState([]);
  const [busca, setBusca] = useState('');
  const [periodo, setPeriodo] = useState({
    de: filtroInicial?.competencia || filtroInicial?.de || mesAtual(),
    ate: filtroInicial?.competencia || filtroInicial?.ate || mesAtual(),
  });
  const [mostrarArquivadas, setMostrarArquivadas] = useState(false);
  const [showNovo, setShowNovo] = useState(!!abrirCriacao);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [selecionados, setSelecionados] = useState([]);
  const [arquivandoLote, setArquivandoLote] = useState(false);
  // Item 20 do pedido de drill-down: quando se chega aqui a partir de um
  // KPI/gráfico do dashboard executivo, esses filtros vêm pré-marcados —
  // inclusive o período (mês único ou intervalo), não só status/tipo/
  // cliente (bug real: clicar em "Vencido" filtrado em Julho abria a
  // Aba Receitas já no mês vigente, perdendo o contexto do clique).
  const [tipoFiltro, setTipoFiltro] = useState(filtroInicial?.tipo || '');
  const [statusFiltro, setStatusFiltro] = useState(filtroInicial?.status || '');
  const [clienteFiltro, setClienteFiltro] = useState(filtroInicial?.clienteId || '');
  const [projetoFiltro, setProjetoFiltro] = useState(filtroInicial?.projetoId || '');
  const [vencimentoDe, setVencimentoDe] = useState(filtroInicial?.vencimentoDe || '');
  const [vencimentoAte, setVencimentoAte] = useState(filtroInicial?.vencimentoAte || '');

  const carregar = useCallback(async () => {
    const params = new URLSearchParams({ status: statusFiltro });
    if (mostrarArquivadas) params.set('incluir_arquivados', '1');
    if (periodo.de) params.set('competencia_de', periodo.de);
    if (periodo.ate) params.set('competencia_ate', periodo.ate);
    if (clienteFiltro) params.set('cliente_id', clienteFiltro);
    if (projetoFiltro) params.set('projeto_id', projetoFiltro);
    if (vencimentoDe) params.set('vencimento_de', vencimentoDe);
    if (vencimentoAte) params.set('vencimento_ate', vencimentoAte);
    const r = await apiFin(`/receivables?${params}`);
    setLinhas((r.lista || []).filter((x) => !tipoFiltro || x.tipo === tipoFiltro));
  }, [mostrarArquivadas, periodo, statusFiltro, clienteFiltro, projetoFiltro, tipoFiltro, vencimentoDe, vencimentoAte]);

  useEffect(() => {
    carregar();
    apiClientesFn('').then((r) => setClientes(r.lista || []));
    apiJson('/projetos').then((r) => { if (r.status === 'ok') setProjetos(r.lista || []); });
  }, [carregar]);
  useEffect(() => { setSelecionados([]); }, [mostrarArquivadas, periodo, statusFiltro, clienteFiltro, projetoFiltro, tipoFiltro]);

  const filtradas = (linhas || []).filter((l) => !busca || l.descricao.toLowerCase().includes(busca.toLowerCase()) || (l.cliente_nome || '').toLowerCase().includes(busca.toLowerCase()));

  const alternarSelecao = (id) => setSelecionados((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));
  const arquivarSelecionados = async () => {
    setArquivandoLote(true);
    await Promise.all(selecionados.map((id) => apiFin(`/receivables/${id}/arquivar`, { method: 'POST' })));
    setArquivandoLote(false);
    setSelecionados([]);
    showToast(`${selecionados.length} receita(s) arquivada(s)`, 'success');
    carregar();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--crm-space-3)', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-3)', flexWrap: 'wrap' }}>
          <div className="crm-search-input" style={{ minWidth: '220px' }}>
            <FontAwesomeIcon icon={faSearch} />
            <input className="crm-input" placeholder="Buscar..." value={busca} onChange={(e) => setBusca(e.target.value)} />
          </div>
          <PeriodFilterBar
            mesInicial={filtroInicial?.competencia}
            deInicial={filtroInicial?.competencia || filtroInicial?.de}
            ateInicial={filtroInicial?.competencia || filtroInicial?.ate}
            onChange={(de, ate) => setPeriodo({ de, ate })}
          />
          <select className="crm-select" value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)} style={{ maxWidth: '140px' }}>
            <option value="">Todos os tipos</option>
            <option value="AVULSO">Avulso</option>
            <option value="ASSINATURA">Assinatura</option>
          </select>
          <select className="crm-select" value={statusFiltro} onChange={(e) => setStatusFiltro(e.target.value)} style={{ maxWidth: '150px' }}>
            <option value="">Todos os status</option>
            <option value="PENDENTE">Pendente</option>
            <option value="PARCIAL">Parcial</option>
            <option value="PAGO">Pago</option>
            <option value="VENCIDO">Vencido</option>
            <option value="CANCELADO">Cancelado</option>
          </select>
          <select className="crm-select" value={clienteFiltro} onChange={(e) => setClienteFiltro(e.target.value)} style={{ maxWidth: '180px' }}>
            <option value="">Todos os clientes</option>
            {clientes.map((c) => <option key={c._id} value={c._id}>{c.nome}</option>)}
          </select>
          <select className="crm-select" value={projetoFiltro} onChange={(e) => setProjetoFiltro(e.target.value)} style={{ maxWidth: '180px' }}>
            <option value="">Todos os projetos</option>
            {projetos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
          {(vencimentoDe || vencimentoAte) && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)' }}>
              <Badge tone="accent">Vencimento {fmtDate(vencimentoDe)} – {fmtDate(vencimentoAte)}</Badge>
              <Button size="sm" variant="ghost" onClick={() => { setVencimentoDe(''); setVencimentoAte(''); }}>Limpar</Button>
            </label>
          )}
          <SegmentedControl
            size="sm"
            value={mostrarArquivadas}
            onChange={setMostrarArquivadas}
            options={[{ value: false, label: 'Ativas' }, { value: true, label: 'Todas' }]}
          />
        </div>
        <Button variant="primary" icon={faPlus} onClick={() => setShowNovo(true)}>Receita</Button>
      </div>
      <BulkActionBar count={selecionados.length} processando={arquivandoLote} onArquivar={arquivarSelecionados} onLimpar={() => setSelecionados([])} />
      <Panel flush>
        {linhas === null ? <EmptyState title="Carregando..." /> : filtradas.length === 0 ? (
          <EmptyState title="Nenhuma receita encontrada" description="Ajuste os filtros ou cadastre uma nova receita." actions={<Button variant="primary" icon={faPlus} onClick={() => setShowNovo(true)}>Receita</Button>} />
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead><tr><th style={{ width: '32px' }} /><th>Descrição</th><th>Tipo</th><th>Cliente</th><th>Projeto</th><th>Competência</th><th>Vencimento</th><th>Status</th><th>Valor</th><th>Pago</th><th>Saldo</th><th className="crm-table-actions-cell" /></tr></thead>
              <tbody>
                {filtradas.map((l) => (
                  <LinhaReceita
                    key={l.id}
                    l={l}
                    selecionado={selecionados.includes(l.id)}
                    onAlternarSelecao={() => alternarSelecao(l.id)}
                    onAbrir={() => setItemSelecionado({ ...l, _origem: 'receivable' })}
                    onNavegar={onNavegar}
                    showToast={showToast}
                    onMudou={carregar}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {showNovo && <LancamentoCreateSheet tipo="RECEITA" clientes={clientes} clienteIdInicial={clienteIdContexto} onClose={() => setShowNovo(false)} onCreated={carregar} showToast={showToast} />}
      {itemSelecionado && <LancamentoSheet item={itemSelecionado} onClose={() => setItemSelecionado(null)} onChanged={carregar} showToast={showToast} onNavegar={onNavegar} onAbrirAssinatura={onAbrirAssinatura} />}
    </div>
  );
}

// Linha de Receita com menu rápido (§7/§8/§9/§23 da evolução de produto):
// registrar pagamento/vincular projeto/duplicar em no máximo 2 ações a
// partir da própria tabela, sem abrir o drawer completo. Saldo/Pago
// aparecem direto na tabela (nunca só escondidos dentro do drawer).
function LinhaReceita({ l, selecionado, onAlternarSelecao, onAbrir, onNavegar, showToast, onMudou }) {
  const [pagamentoAberto, setPagamentoAberto] = useState(false);
  const [vincularAberto, setVincularAberto] = useState(false);
  const anchorRef = useRef(null);
  const podePagar = l.situacao !== 'PAGO' && l.situacao !== 'CANCELADO';
  const saldo = parseFloat(l.saldo ?? l.valor);

  const duplicar = async () => {
    const r = await apiFin(`/receivables/${l.id}/duplicar`, { method: 'POST' });
    if (r.status === 'ok') { showToast('Receita duplicada — vencimento hoje, revise antes de cobrar.', 'success'); onMudou(); }
    else showToast(r.mensagem || 'Erro ao duplicar', 'error');
  };
  const arquivar = async () => {
    const r = await apiFin(`/receivables/${l.id}/arquivar`, { method: 'POST' });
    if (r.status === 'ok') { showToast('Receita arquivada', 'success'); onMudou(); }
  };
  const vincular = async (projetoId) => {
    const r = await apiFin(`/receivables/${l.id}`, { method: 'PUT', body: JSON.stringify({ projeto_id: projetoId }) });
    if (r.status === 'ok' || r.receivable) { showToast(projetoId ? 'Projeto vinculado!' : 'Vínculo removido', 'success'); onMudou(); }
    setVincularAberto(false);
  };

  return (
    <>
    <tr className="clickable" onClick={onAbrir}>
      <td onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selecionado} onChange={onAlternarSelecao} /></td>
      <td className="crm-table-primary">{l.descricao}{l.arquivado_em && <span style={{ marginLeft: 'var(--crm-space-2)' }}><Badge tone="neutral">Arquivada</Badge></span>}</td>
      <td><Badge tone="neutral">{l.tipo === 'ASSINATURA' ? 'Assinatura' : 'Avulso'}</Badge></td>
      <td>{l.cliente_id ? <EntityLink onClick={(e) => { e.stopPropagation(); onNavegar?.('clientes', { clienteId: l.cliente_id }); }}>{l.cliente_nome || 'Cliente'}</EntityLink> : '—'}</td>
      <td ref={anchorRef} onClick={(e) => e.stopPropagation()}>
        {l.projeto_id ? (
          <EntityLink onClick={() => onNavegar?.('projetos', { projetoId: l.projeto_id })}>{l.projeto_nome || 'Projeto'}</EntityLink>
        ) : (
          <button type="button" className="crm-entity-link" onClick={() => setVincularAberto((v) => !v)}>+ Vincular</button>
        )}
        {vincularAberto && (
          <VincularProjetoSelector anchorRef={anchorRef} clienteId={l.cliente_id} projetoAtualId={l.projeto_id} onSelecionar={vincular} onFechar={() => setVincularAberto(false)} />
        )}
      </td>
      <td style={{ textTransform: 'capitalize' }}>{nomeCompetencia(l.competencia)}</td>
      <td>{fmtDate(l.vencimento)}</td>
      <td><StatusBadge status={l.situacao} /></td>
      <td>{fmtBRL(l.valor)}</td>
      <td>{fmtBRL(l.valor_pago)}</td>
      <td style={{ fontWeight: 600 }}>{fmtBRL(saldo)}</td>
      <td className="crm-table-actions-cell" onClick={(e) => e.stopPropagation()}>
        <RowMenu itens={[
          { label: 'Abrir', onClick: onAbrir },
          { label: 'Registrar pagamento', onClick: () => setPagamentoAberto(true), visivel: podePagar },
          { label: 'Vincular projeto', onClick: () => setVincularAberto(true), visivel: !l.projeto_id },
          { label: 'Duplicar', onClick: duplicar },
          { label: 'Arquivar', onClick: arquivar, visivel: !l.arquivado_em },
        ]} />
      </td>
    </tr>
    {pagamentoAberto && (
      <RegistrarPagamentoDialog item={{ ...l, _origem: 'receivable' }} onClose={() => setPagamentoAberto(false)} onChanged={onMudou} showToast={showToast} />
    )}
    </>
  );
}

// ─── Despesas (§24) ──────────────────────────────────────────────────────
function AbaDespesas({ showToast, onNavegar, filtroInicial }) {
  const [linhas, setLinhas] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [projetos, setProjetos] = useState([]);
  const [busca, setBusca] = useState('');
  const [periodo, setPeriodo] = useState({
    de: filtroInicial?.competencia || filtroInicial?.de || mesAtual(),
    ate: filtroInicial?.competencia || filtroInicial?.ate || mesAtual(),
  });
  const [categoriaFiltro, setCategoriaFiltro] = useState(filtroInicial?.categoria || '');
  const [clienteFiltro, setClienteFiltro] = useState('');
  const [projetoFiltro, setProjetoFiltro] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('');
  const [mostrarArquivadas, setMostrarArquivadas] = useState(false);
  const [showNovo, setShowNovo] = useState(false);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [assinaturaAberta, setAssinaturaAberta] = useState(null);
  const [selecionados, setSelecionados] = useState([]);
  const [arquivandoLote, setArquivandoLote] = useState(false);

  const carregar = useCallback(async () => {
    const params = new URLSearchParams();
    if (mostrarArquivadas) params.set('incluir_arquivados', '1');
    if (periodo.de) params.set('competencia_de', periodo.de);
    if (periodo.ate) params.set('competencia_ate', periodo.ate);
    if (categoriaFiltro) params.set('categoria', categoriaFiltro);
    if (clienteFiltro) params.set('cliente_id', clienteFiltro);
    if (projetoFiltro) params.set('projeto_id', projetoFiltro);
    if (statusFiltro) params.set('status', statusFiltro);
    const qs = params.toString();
    const r = await apiFin(`/payables${qs ? `?${qs}` : ''}`);
    setLinhas((r.lista || []).filter((x) => x.tipo !== 'FOLHA' && x.tipo !== 'PROLABORE'));
  }, [mostrarArquivadas, periodo, categoriaFiltro, clienteFiltro, projetoFiltro, statusFiltro]);
  useEffect(() => {
    carregar();
    apiClientesFn('').then((r) => setClientes(r.lista || []));
    apiJson('/projetos').then((r) => { if (r.status === 'ok') setProjetos(r.lista || []); });
  }, [carregar]);
  useEffect(() => { setSelecionados([]); }, [mostrarArquivadas, periodo, categoriaFiltro, clienteFiltro, projetoFiltro, statusFiltro]);

  const filtradas = (linhas || []).filter((l) => !busca || l.descricao.toLowerCase().includes(busca.toLowerCase()));

  const alternarSelecao = (id) => setSelecionados((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));
  const arquivarSelecionados = async () => {
    setArquivandoLote(true);
    await Promise.all(selecionados.map((id) => apiFin(`/payables/${id}/arquivar`, { method: 'POST' })));
    setArquivandoLote(false);
    setSelecionados([]);
    showToast(`${selecionados.length} despesa(s) arquivada(s)`, 'success');
    carregar();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--crm-space-3)', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-3)', flexWrap: 'wrap' }}>
          <div className="crm-search-input" style={{ minWidth: '220px' }}>
            <FontAwesomeIcon icon={faSearch} />
            <input className="crm-input" placeholder="Buscar..." value={busca} onChange={(e) => setBusca(e.target.value)} />
          </div>
          <PeriodFilterBar
            mesInicial={filtroInicial?.competencia}
            deInicial={filtroInicial?.competencia || filtroInicial?.de}
            ateInicial={filtroInicial?.competencia || filtroInicial?.ate}
            onChange={(de, ate) => setPeriodo({ de, ate })}
          />
          <select className="crm-select" value={clienteFiltro} onChange={(e) => setClienteFiltro(e.target.value)} style={{ maxWidth: '180px' }}>
            <option value="">Todos os clientes</option>
            {clientes.map((c) => <option key={c._id} value={c._id}>{c.nome}</option>)}
          </select>
          <select className="crm-select" value={projetoFiltro} onChange={(e) => setProjetoFiltro(e.target.value)} style={{ maxWidth: '180px' }}>
            <option value="">Todos os projetos</option>
            {projetos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
          <select className="crm-select" value={statusFiltro} onChange={(e) => setStatusFiltro(e.target.value)} style={{ maxWidth: '150px' }}>
            <option value="">Todos os status</option>
            <option value="PENDENTE">Pendente</option>
            <option value="PARCIAL">Parcial</option>
            <option value="PAGO">Pago</option>
            <option value="VENCIDO">Vencido</option>
            <option value="CANCELADO">Cancelado</option>
          </select>
          {categoriaFiltro && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)' }}>
              <span className="crm-form-hint">Categoria: {categoriaFiltro}</span>
              <Button size="sm" variant="ghost" onClick={() => setCategoriaFiltro('')}>Limpar</Button>
            </label>
          )}
          <SegmentedControl
            size="sm"
            value={mostrarArquivadas}
            onChange={setMostrarArquivadas}
            options={[{ value: false, label: 'Ativas' }, { value: true, label: 'Todas' }]}
          />
        </div>
        <Button variant="primary" icon={faPlus} onClick={() => setShowNovo(true)}>Despesa</Button>
      </div>
      <BulkActionBar count={selecionados.length} processando={arquivandoLote} onArquivar={arquivarSelecionados} onLimpar={() => setSelecionados([])} />
      <Panel flush>
        {linhas === null ? <EmptyState title="Carregando..." /> : filtradas.length === 0 ? (
          <EmptyState title="Nenhuma despesa encontrada" actions={<Button variant="primary" icon={faPlus} onClick={() => setShowNovo(true)}>Despesa</Button>} />
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead><tr><th style={{ width: '32px' }} /><th>Descrição</th><th>Fornecedor</th><th>Projeto</th><th>Competência</th><th>Vencimento</th><th>Status</th><th>Origem</th><th>Valor</th><th>Pago</th><th>Saldo</th><th className="crm-table-actions-cell" /></tr></thead>
              <tbody>
                {filtradas.map((l) => (
                  <LinhaDespesa
                    key={l.id}
                    l={l}
                    selecionado={selecionados.includes(l.id)}
                    onAlternarSelecao={() => alternarSelecao(l.id)}
                    onAbrir={() => setItemSelecionado({ ...l, _origem: 'payable' })}
                    onNavegar={onNavegar}
                    showToast={showToast}
                    onMudou={carregar}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {showNovo && <LancamentoCreateSheet tipo="DESPESA" clientes={[]} onClose={() => setShowNovo(false)} onCreated={carregar} showToast={showToast} />}
      {itemSelecionado && <LancamentoSheet item={itemSelecionado} onClose={() => setItemSelecionado(null)} onChanged={carregar} showToast={showToast} onNavegar={onNavegar} onAbrirAssinatura={setAssinaturaAberta} />}
      <AssinaturaSheet assinaturaId={assinaturaAberta} open={!!assinaturaAberta} onClose={() => setAssinaturaAberta(null)} onChanged={carregar} showToast={showToast} onNavegar={onNavegar} />
    </div>
  );
}

// Linha de Despesa — mesmo princípio de `LinhaReceita` (§7/§8/§9/§23):
// Pago/Saldo direto na tabela, registrar pagamento/vincular/duplicar em
// no máximo 2 ações.
function LinhaDespesa({ l, selecionado, onAlternarSelecao, onAbrir, onNavegar, showToast, onMudou }) {
  const [pagamentoAberto, setPagamentoAberto] = useState(false);
  const [vincularAberto, setVincularAberto] = useState(false);
  const anchorRef = useRef(null);
  const podePagar = l.situacao !== 'PAGO' && l.situacao !== 'CANCELADO';
  const saldo = parseFloat(l.saldo ?? l.valor);

  const duplicar = async () => {
    const r = await apiFin(`/payables/${l.id}/duplicar`, { method: 'POST' });
    if (r.status === 'ok') { showToast('Despesa duplicada — vencimento hoje, revise antes de pagar.', 'success'); onMudou(); }
    else showToast(r.mensagem || 'Erro ao duplicar', 'error');
  };
  const arquivar = async () => {
    const r = await apiFin(`/payables/${l.id}/arquivar`, { method: 'POST' });
    if (r.status === 'ok') { showToast('Despesa arquivada', 'success'); onMudou(); }
  };
  const vincular = async (projetoId) => {
    const r = await apiFin(`/payables/${l.id}`, { method: 'PUT', body: JSON.stringify({ projeto_id: projetoId }) });
    if (r.status === 'ok' || r.payable) { showToast(projetoId ? 'Projeto vinculado!' : 'Vínculo removido', 'success'); onMudou(); }
    setVincularAberto(false);
  };

  return (
    <>
    <tr className="clickable" onClick={onAbrir}>
      <td onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selecionado} onChange={onAlternarSelecao} /></td>
      <td className="crm-table-primary">{l.descricao}{l.arquivado_em && <span style={{ marginLeft: 'var(--crm-space-2)' }}><Badge tone="neutral">Arquivada</Badge></span>}</td>
      <td>{l.fornecedor_nome || '—'}</td>
      <td ref={anchorRef} onClick={(e) => e.stopPropagation()}>
        {l.projeto_id ? (
          <EntityLink onClick={() => onNavegar?.('projetos', { projetoId: l.projeto_id })}>{l.projeto_nome || 'Projeto'}</EntityLink>
        ) : (
          <button type="button" className="crm-entity-link" onClick={() => setVincularAberto((v) => !v)}>+ Vincular</button>
        )}
        {vincularAberto && (
          <VincularProjetoSelector anchorRef={anchorRef} clienteId={l.cliente_id} projetoAtualId={l.projeto_id} onSelecionar={vincular} onFechar={() => setVincularAberto(false)} />
        )}
      </td>
      <td style={{ textTransform: 'capitalize' }}>{nomeCompetencia(l.competencia)}</td>
      <td>{fmtDate(l.vencimento)}</td>
      <td><StatusBadge status={l.situacao} /></td>
      <td>{l.compra_id ? <Badge tone="accent">Compra</Badge> : '—'}</td>
      <td>{fmtBRL(l.valor)}</td>
      <td>{fmtBRL(l.valor_pago)}</td>
      <td style={{ fontWeight: 600 }}>{fmtBRL(saldo)}</td>
      <td className="crm-table-actions-cell" onClick={(e) => e.stopPropagation()}>
        <RowMenu itens={[
          { label: 'Abrir', onClick: onAbrir },
          { label: 'Registrar pagamento', onClick: () => setPagamentoAberto(true), visivel: podePagar },
          { label: 'Vincular projeto', onClick: () => setVincularAberto(true), visivel: !l.projeto_id },
          { label: 'Duplicar', onClick: duplicar, visivel: l.tipo === 'DESPESA' },
          { label: 'Arquivar', onClick: arquivar, visivel: !l.arquivado_em },
        ]} />
      </td>
    </tr>
    {pagamentoAberto && (
      <RegistrarPagamentoDialog item={{ ...l, _origem: 'payable' }} onClose={() => setPagamentoAberto(false)} onChanged={onMudou} showToast={showToast} />
    )}
    </>
  );
}

// ─── Assinaturas (§17) ───────────────────────────────────────────────────
const STATUS_ASSINATURA_TONE_LISTA = { ATIVA: 'positive', PAUSADA: 'warning', CANCELADA: 'neutral', ARQUIVADA: 'neutral' };

function AbaAssinaturas({ showToast, clienteIdContexto, abrirCriacao, onAbrirAssinatura, filtroInicial, onNavegar }) {
  const [regras, setRegras] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [busca, setBusca] = useState('');
  const [competencia, setCompetencia] = useState(filtroInicial?.competencia || mesAtual());
  const [mostrarArquivadas, setMostrarArquivadas] = useState(false);
  const [statusFiltro, setStatusFiltro] = useState(filtroInicial?.status || '');
  const [clienteFiltro, setClienteFiltro] = useState('');
  const [showNovo, setShowNovo] = useState(!!abrirCriacao);
  const [form, setForm] = useState({ cliente_id: clienteIdContexto || '', descricao: '', valor: '', dia_vencimento: '10', forma_pagamento: '', data_inicio: hoje() });
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    const params = new URLSearchParams();
    if (mostrarArquivadas) params.set('incluir_arquivadas', '1');
    if (competencia) params.set('competencia', competencia);
    if (statusFiltro) params.set('status', statusFiltro);
    if (clienteFiltro) params.set('cliente_id', clienteFiltro);
    const r = await apiFin(`/recurrence-rules?${params}`);
    setRegras(r.lista || []);
  }, [mostrarArquivadas, competencia, statusFiltro, clienteFiltro]);
  useEffect(() => { carregar(); apiClientesFn('').then((r) => setClientes(r.lista || [])); }, [carregar]);

  const criar = async () => {
    if (!form.cliente_id || !form.descricao.trim() || !form.valor) { showToast('Preencha cliente, descrição e valor', 'error'); return; }
    setSalvando(true);
    const r = await apiFin('/recurrence-rules', { method: 'POST', body: JSON.stringify({ tipo: 'RECEITA', ...form, dia_vencimento: parseInt(form.dia_vencimento, 10) }) });
    setSalvando(false);
    if (r.status === 'ok') { showToast('Assinatura criada!', 'success'); setShowNovo(false); carregar(); }
    else showToast(r.mensagem || 'Erro ao criar assinatura', 'error');
  };

  const rotuloStatus = (s) => (s === 'ATIVA' ? 'Ativa' : s === 'PAUSADA' ? 'Pausada' : s === 'ARQUIVADA' ? 'Arquivada' : 'Cancelada');

  const regrasFiltradas = (regras || []).filter((r) => {
    if (!busca) return true;
    const termo = busca.toLowerCase();
    const nomeCliente = clientes.find((c) => c._id === r.cliente_id)?.nome || r.cliente_nome || '';
    return r.descricao.toLowerCase().includes(termo) || nomeCliente.toLowerCase().includes(termo);
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--crm-space-3)', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-3)', flexWrap: 'wrap' }}>
          <div className="crm-search-input" style={{ minWidth: '220px' }}>
            <FontAwesomeIcon icon={faSearch} />
            <input className="crm-input" placeholder="Buscar..." value={busca} onChange={(e) => setBusca(e.target.value)} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)' }}>
            <span className="crm-form-hint" style={{ textTransform: 'capitalize' }}>Mês: {nomeCompetencia(competencia)}</span>
            <MonthPicker value={competencia} onChange={setCompetencia} />
          </label>
          <select className="crm-select" value={statusFiltro} onChange={(e) => setStatusFiltro(e.target.value)} style={{ maxWidth: '160px' }}>
            <option value="">Todos os status</option>
            <option value="ATIVA">Ativa</option>
            <option value="PAUSADA">Pausada</option>
            <option value="CANCELADA">Cancelada</option>
          </select>
          <select className="crm-select" value={clienteFiltro} onChange={(e) => setClienteFiltro(e.target.value)} style={{ maxWidth: '180px' }}>
            <option value="">Todos os clientes</option>
            {clientes.map((c) => <option key={c._id} value={c._id}>{c.nome}</option>)}
          </select>
          <SegmentedControl
            size="sm"
            value={mostrarArquivadas}
            onChange={setMostrarArquivadas}
            options={[{ value: false, label: 'Ativas' }, { value: true, label: 'Todas' }]}
          />
        </div>
        <Button variant="primary" icon={faPlus} onClick={() => setShowNovo(true)}>Assinatura</Button>
      </div>
      <Panel flush>
        {regras === null ? <EmptyState title="Carregando..." /> : regrasFiltradas.length === 0 ? (
          <EmptyState title="Nenhuma assinatura encontrada" description="Assinaturas geram cobranças recorrentes automaticamente (MRR)." actions={<Button variant="primary" icon={faPlus} onClick={() => setShowNovo(true)}>Assinatura</Button>} />
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead><tr><th>Cliente</th><th>Plano</th><th>Projeto</th><th>Valor/mês</th><th>Vencimento</th><th>Assinatura</th><th style={{ textTransform: 'capitalize' }}>{nomeCompetencia(competencia)}</th><th>Cobrança automática</th><th className="crm-table-actions-cell" /></tr></thead>
              <tbody>
                {regrasFiltradas.map((r) => (
                  <LinhaAssinatura key={r.id} r={r} cliente={clientes.find((c) => c._id === r.cliente_id)} onAbrir={() => onAbrirAssinatura(r.id)} onNavegar={onNavegar} showToast={showToast} onMudou={carregar} rotuloStatus={rotuloStatus} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Sheet open={showNovo} onClose={() => setShowNovo(false)} title="Nova Assinatura" footer={<Button variant="primary" loading={salvando} onClick={criar}>Criar</Button>}>
        <div className="crm-form-field">
          <label className="crm-form-label">Cliente</label>
          <select className="crm-select" value={form.cliente_id} onChange={(e) => setForm({ ...form, cliente_id: e.target.value })}>
            <option value="">Selecione...</option>
            {clientes.map((c) => <option key={c._id} value={c._id}>{c.nome}</option>)}
          </select>
        </div>
        <div className="crm-form-field"><label className="crm-form-label">Plano/Descrição</label><input className="crm-input" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} /></div>
        <div className="crm-form-field"><label className="crm-form-label">Valor</label><input className="crm-input" type="number" step="0.01" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} /></div>
        <div className="crm-form-field"><label className="crm-form-label">Dia de vencimento</label><input className="crm-input" type="number" min="1" max="31" value={form.dia_vencimento} onChange={(e) => setForm({ ...form, dia_vencimento: e.target.value })} /></div>
        <div className="crm-form-field"><label className="crm-form-label">Forma de pagamento</label><input className="crm-input" value={form.forma_pagamento} onChange={(e) => setForm({ ...form, forma_pagamento: e.target.value })} /></div>
        <div className="crm-form-field"><label className="crm-form-label">Data de início</label><input className="crm-input" type="date" value={form.data_inicio} onChange={(e) => setForm({ ...form, data_inicio: e.target.value })} /></div>
      </Sheet>
    </div>
  );
}

// Linha de Assinatura com informação operacional em vez de um badge que
// parece status financeiro (§2/§12/§13/§20 da evolução de produto):
// competência ainda não materializada nunca aparece como "Pendente"/
// "Vencido" — só como "Gerar agora" (exceção, já deveria ter sido gerada)
// ou "Próxima geração: DD/MM" (normal, dentro do horizonte automático).
function LinhaAssinatura({ r, cliente, onAbrir, onNavegar, showToast, onMudou, rotuloStatus }) {
  const gerarAgora = async () => {
    const comp = r.competencia_consultada;
    const res = await apiFin(`/recurrence-rules/${r.id}/gerar-historico`, {
      method: 'POST',
      body: JSON.stringify({ competencia_inicio: comp, competencia_fim: comp, status: 'PENDENTE' }),
    });
    if (res.status === 'ok') { showToast('Competência gerada', 'success'); onMudou(); }
    else showToast(res.mensagem || 'Erro ao gerar competência', 'error');
  };
  const gerarProximos = async () => {
    const res = await apiFin(`/recurrence-rules/${r.id}/gerar-futuro`, { method: 'POST', body: JSON.stringify({ meses: 12 }) });
    if (res.status === 'ok') { showToast('Próximas competências geradas', 'success'); onMudou(); }
    else showToast(res.mensagem || 'Erro ao gerar', 'error');
  };
  const pausar = async () => {
    const res = await apiFin(`/recurrence-rules/${r.id}/pausar`, { method: 'POST' });
    if (res.status === 'ok') { showToast('Assinatura pausada', 'success'); onMudou(); }
  };
  const retomar = async () => {
    const res = await apiFin(`/recurrence-rules/${r.id}/retomar`, { method: 'POST' });
    if (res.status === 'ok') { showToast('Assinatura retomada', 'success'); onMudou(); }
  };

  return (
    <tr className="clickable" onClick={onAbrir}>
      <td className="crm-table-primary">{cliente?.nome || '—'}</td>
      <td>{r.descricao}</td>
      <td>{r.projeto_id ? <EntityLink onClick={(e) => { e.stopPropagation(); onNavegar?.('projetos', { projetoId: r.projeto_id }); }}>{r.projeto_nome}</EntityLink> : '—'}</td>
      <td>{fmtBRL(r.valor)}</td>
      <td>dia {r.dia_vencimento}</td>
      <td><Badge tone={STATUS_ASSINATURA_TONE_LISTA[r.status]}>{rotuloStatus(r.status)}</Badge></td>
      <td onClick={(e) => e.stopPropagation()}>
        {r.competencia_gerada ? (
          <div>
            <StatusBadge status={r.competencia_status} />
            <div className="crm-form-hint" style={{ marginTop: 2 }}>{fmtBRL(r.competencia_saldo)} em aberto</div>
          </div>
        ) : r.competencia_excecao_nao_gerada ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)' }}>
            <Badge tone="warning">⚠ Não gerada</Badge>
            <Button size="sm" variant="outline" onClick={gerarAgora}>Gerar agora</Button>
          </div>
        ) : (
          <span className="crm-form-hint">Próxima geração: {fmtDate(r.competencia_proxima_geracao)}</span>
        )}
      </td>
      <td>
        {!r.competencia_automacao ? <span className="crm-form-hint">—</span> : (
          <Badge tone={r.competencia_automacao === 'ATIVA' ? 'positive' : 'warning'}>{r.competencia_automacao === 'ATIVA' ? 'Ativa' : 'Pausada'}</Badge>
        )}
      </td>
      <td className="crm-table-actions-cell" onClick={(e) => e.stopPropagation()}>
        <RowMenu itens={[
          { label: 'Abrir', onClick: onAbrir },
          { label: 'Gerar competência', onClick: gerarAgora, visivel: !r.competencia_gerada },
          { label: 'Gerar próximos 12 meses', onClick: gerarProximos },
          { label: 'Pausar', onClick: pausar, visivel: r.status === 'ATIVA' },
          { label: 'Retomar', onClick: retomar, visivel: r.status === 'PAUSADA' },
        ]} />
      </td>
    </tr>
  );
}

// ─── Cobranças automáticas (§22) ─────────────────────────────────────────
const STATUS_AUTOMACAO_TONE = { ATIVA: 'positive', PAUSADA: 'warning', CANCELADA: 'neutral', ARQUIVADA: 'neutral' };
const FILTROS_AUTOMACAO = [
  { id: 'ATIVA', label: 'Ativas' }, { id: 'PAUSADA', label: 'Pausadas' }, { id: 'CANCELADA', label: 'Canceladas' },
  { id: 'ARQUIVADA', label: 'Arquivadas' }, { id: 'TODAS', label: 'Todas' },
];

function AbaCobrancas({ showToast, onNavegar, onAbrirAssinatura, filtroInicial }) {
  const [agenda, setAgenda] = useState(null);
  const [filtro, setFiltro] = useState(filtroInicial || 'ATIVA');
  const [periodo, setPeriodo] = useState({ de: mesAtual(), ate: mesAtual() });
  const [clientes, setClientes] = useState([]);
  const [projetos, setProjetos] = useState([]);
  const [clienteFiltro, setClienteFiltro] = useState('');
  const [projetoFiltro, setProjetoFiltro] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [showWizard, setShowWizard] = useState(false);
  const [editando, setEditando] = useState(null);
  const [contaSelecionada, setContaSelecionada] = useState(null);

  const carregar = useCallback(() => {
    const params = new URLSearchParams({ status: filtro });
    if (periodo.de) params.set('competencia_de', periodo.de);
    if (periodo.ate) params.set('competencia_ate', periodo.ate);
    if (clienteFiltro) params.set('cliente_id', clienteFiltro);
    if (projetoFiltro) params.set('projeto_id', projetoFiltro);
    if (tipoFiltro) params.set('tipo', tipoFiltro);
    apiFin(`/cobrancas/agenda?${params}`).then((r) => { if (r.status === 'ok') setAgenda(r.lista); });
  }, [filtro, periodo, clienteFiltro, projetoFiltro, tipoFiltro]);
  useEffect(() => {
    carregar();
    apiClientesFn('').then((r) => setClientes(r.lista || []));
    apiJson('/projetos').then((r) => { if (r.status === 'ok') setProjetos(r.lista || []); });
  }, [carregar]);

  const pausar = async (receivableId) => {
    const r = await apiFin(`/receivables/${receivableId}/billing-automation/pausar`, { method: 'POST' });
    if (r.status === 'ok') { showToast('Automação pausada', 'success'); carregar(); }
    else showToast(r.mensagem || 'Erro ao pausar', 'error');
  };
  const retomar = async (receivableId) => {
    const r = await apiFin(`/receivables/${receivableId}/billing-automation/retomar`, { method: 'POST' });
    if (r.status === 'ok') { showToast('Automação retomada', 'success'); carregar(); }
    else showToast(r.mensagem || 'Erro ao retomar', 'error');
  };
  const cancelar = async (receivableId) => {
    const { isConfirmed } = await Swal.fire({ title: 'Cancelar esta cobrança automática?', text: 'A automação para de disparar mensagens novas, mas o histórico é preservado.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Cancelar automação', cancelButtonText: 'Voltar' });
    if (!isConfirmed) return;
    const r = await apiFin(`/receivables/${receivableId}/billing-automation/cancelar`, { method: 'POST' });
    if (r.status === 'ok') { showToast('Automação cancelada', 'success'); carregar(); }
    else showToast(r.mensagem || 'Erro ao cancelar', 'error');
  };
  const arquivar = async (receivableId) => {
    const { isConfirmed } = await Swal.fire({ title: 'Arquivar esta cobrança automática?', text: 'Ela some da lista normal, mas nada é apagado.', icon: 'question', showCancelButton: true, confirmButtonText: 'Arquivar', cancelButtonText: 'Voltar' });
    if (!isConfirmed) return;
    const r = await apiFin(`/receivables/${receivableId}/billing-automation/arquivar`, { method: 'POST' });
    if (r.status === 'ok') { showToast('Automação arquivada', 'success'); carregar(); }
    else showToast(r.mensagem || 'Erro ao arquivar', 'error');
  };
  const excluir = async (receivableId, produto) => {
    const { isConfirmed } = await Swal.fire({
      title: 'Excluir definitivamente?', icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Excluir definitivamente', cancelButtonText: 'Voltar', confirmButtonColor: '#dc2626',
      html: `Isso remove por completo a automação de cobrança de <strong>${produto}</strong>.<br/>Só é permitido quando ela nunca teve envio real nem pagamento associado — não pode ser desfeito.`,
    });
    if (!isConfirmed) return;
    const r = await apiFin(`/receivables/${receivableId}/billing-automation/excluir`, { method: 'POST' });
    if (r.status === 'ok') { showToast('Automação excluída definitivamente', 'success'); carregar(); }
    else showToast(r.mensagem || 'Erro ao excluir', 'error');
  };

  const itensMenu = (a) => [
    { label: 'Ver conta (valor/pagamento)', onClick: () => setContaSelecionada({ id: a.receivable_id, descricao: a.produto, tipo: 'AVULSO', _origem: 'receivable' }) },
    { label: 'Editar régua', onClick: () => setEditando(a.receivable_id) },
    a.status_automacao === 'PAUSADA'
      ? { label: 'Reativar', onClick: () => retomar(a.receivable_id) }
      : { label: 'Pausar', onClick: () => pausar(a.receivable_id), visivel: a.status_automacao === 'ATIVA' },
    { label: 'Cancelar', onClick: () => cancelar(a.receivable_id), visivel: a.status_automacao === 'ATIVA' || a.status_automacao === 'PAUSADA' },
    { label: 'Arquivar', onClick: () => arquivar(a.receivable_id), visivel: a.status_automacao !== 'ARQUIVADA' },
    { label: 'Excluir definitivamente', tone: 'danger', onClick: () => excluir(a.receivable_id, a.produto) },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--crm-space-3)', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-3)', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 'var(--crm-space-1)' }}>
            {FILTROS_AUTOMACAO.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`crm-chip${filtro === f.id ? ' crm-chip-active' : ''}`}
                onClick={() => setFiltro(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <PeriodFilterBar onChange={(de, ate) => setPeriodo({ de, ate })} />
          <select className="crm-select" value={clienteFiltro} onChange={(e) => setClienteFiltro(e.target.value)} style={{ maxWidth: '180px' }}>
            <option value="">Todos os clientes</option>
            {clientes.map((c) => <option key={c._id} value={c._id}>{c.nome}</option>)}
          </select>
          <select className="crm-select" value={projetoFiltro} onChange={(e) => setProjetoFiltro(e.target.value)} style={{ maxWidth: '180px' }}>
            <option value="">Todos os projetos</option>
            {projetos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
          <select className="crm-select" value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)} style={{ maxWidth: '150px' }}>
            <option value="">Todos os tipos</option>
            <option value="ASSINATURA">Recorrente</option>
            <option value="AVULSO">Avulsa</option>
          </select>
        </div>
        <Button variant="primary" icon={faPlus} onClick={() => setShowWizard(true)}>Cobrança automática</Button>
      </div>
      <Panel title="Agenda de cobrança" flush>
        {agenda === null ? <EmptyState title="Carregando..." /> : agenda.length === 0 ? (
          <EmptyState title="Nenhuma cobrança nesse filtro" actions={<Button variant="primary" icon={faPlus} onClick={() => setShowWizard(true)}>Cobrança automática</Button>} />
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead><tr><th>Cliente</th><th>Produto</th><th>Valor</th><th>Vencimento</th><th>Status</th><th>Próximo envio</th><th>Última mensagem</th><th>Automação</th><th className="crm-table-actions-cell" /></tr></thead>
              <tbody>
                {agenda.map((a) => (
                  <tr key={a.receivable_id} className="clickable" onClick={() => setEditando(a.receivable_id)}>
                    <td className="crm-table-primary">
                      {a.cliente_id ? (
                        <EntityLink onClick={(e) => { e.stopPropagation(); onNavegar?.('clientes', { clienteId: a.cliente_id }); }}>{a.cliente_nome || 'Cliente'}</EntityLink>
                      ) : (a.cliente_nome || '—')}
                    </td>
                    <td>
                      {a.recurrence_rule_id ? (
                        <EntityLink onClick={(e) => { e.stopPropagation(); onAbrirAssinatura?.(a.recurrence_rule_id); }}>{a.produto}</EntityLink>
                      ) : a.produto}
                    </td>
                    <td>
                      <EntityLink onClick={(e) => { e.stopPropagation(); setContaSelecionada({ id: a.receivable_id, descricao: a.produto, tipo: 'AVULSO', _origem: 'receivable' }); }}>{fmtBRL(a.valor)}</EntityLink>
                    </td>
                    <td>{fmtDate(a.vencimento)}</td>
                    <td><StatusBadge status={a.status} /></td>
                    <td>{a.proximo_envio ? `${fmtDate(a.proximo_envio)} (${a.proxima_etapa})` : '—'}</td>
                    <td className="crm-table-sub">{a.ultima_mensagem ? `${a.ultima_mensagem.slice(0, 40)}...` : '—'}</td>
                    <td><Badge tone={STATUS_AUTOMACAO_TONE[a.status_automacao]}>{a.status_automacao === 'ATIVA' ? 'Ativa' : a.status_automacao === 'PAUSADA' ? 'Pausada' : a.status_automacao === 'CANCELADA' ? 'Cancelada' : 'Arquivada'}</Badge></td>
                    <td className="crm-table-actions-cell" onClick={(e) => e.stopPropagation()}>
                      <RowMenu itens={itensMenu(a)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {(showWizard || editando) && (
        <CobrancaWizardSheet
          receivableIdInicial={editando}
          onClose={() => { setShowWizard(false); setEditando(null); }}
          onSalvo={() => { setShowWizard(false); setEditando(null); carregar(); }}
          showToast={showToast}
        />
      )}
      {contaSelecionada && (
        <LancamentoSheet
          item={contaSelecionada}
          onClose={() => setContaSelecionada(null)}
          onChanged={carregar}
          showToast={showToast}
          onNavegar={onNavegar}
          onAbrirAssinatura={onAbrirAssinatura}
        />
      )}
    </div>
  );
}

// ─── Folha (§25) ─────────────────────────────────────────────────────────
function AbaFolha({ showToast, onNavegar }) {
  const [pessoas, setPessoas] = useState(null);
  const [lancamentos, setLancamentos] = useState(null);
  const [showPessoa, setShowPessoa] = useState(false);
  const [novaPessoa, setNovaPessoa] = useState({ nome: '', funcao: '', tipo: 'Colaborador' });
  const [pessoaEditando, setPessoaEditando] = useState(null);
  const [formPessoa, setFormPessoa] = useState({});
  const [salvandoPessoa, setSalvandoPessoa] = useState(false);
  const [showLancamento, setShowLancamento] = useState(false);
  const [form, setForm] = useState({ pessoa_folha_id: '', valor: '', bonus: '0', descontos: '0', vencimento: hoje() });
  const [salvando, setSalvando] = useState(false);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [competencia, setCompetencia] = useState('');
  const [pessoaFiltro, setPessoaFiltro] = useState('');

  const carregar = useCallback(() => {
    apiFin('/pessoas-folha').then((r) => setPessoas(r.lista || []));
    const params = new URLSearchParams({ tipo: 'FOLHA' });
    if (competencia) params.set('competencia', competencia);
    if (pessoaFiltro) params.set('pessoa_folha_id', pessoaFiltro);
    apiFin(`/payables?${params}`).then((r) => setLancamentos(r.lista || []));
  }, [competencia, pessoaFiltro]);
  useEffect(() => { carregar(); }, [carregar]);

  const criarPessoa = async () => {
    if (!novaPessoa.nome.trim()) { showToast('Informe o nome', 'error'); return; }
    const r = await apiFin('/pessoas-folha', { method: 'POST', body: JSON.stringify(novaPessoa) });
    if (r.status === 'ok') { showToast('Pessoa cadastrada!', 'success'); setShowPessoa(false); setNovaPessoa({ nome: '', funcao: '', tipo: 'Colaborador' }); carregar(); }
    else showToast(r.mensagem || 'Erro', 'error');
  };

  const abrirEdicaoPessoa = (p) => {
    setPessoaEditando(p);
    setFormPessoa({ nome: p.nome, funcao: p.funcao || '', tipo: p.tipo, status: p.status });
  };

  const salvarPessoa = async () => {
    setSalvandoPessoa(true);
    const r = await apiFin(`/pessoas-folha/${pessoaEditando.id}`, { method: 'PUT', body: JSON.stringify(formPessoa) });
    setSalvandoPessoa(false);
    if (r.status === 'ok') { showToast('Pessoa atualizada!', 'success'); setPessoaEditando(null); carregar(); }
    else showToast(r.mensagem || 'Erro ao salvar', 'error');
  };

  const inativarPessoa = async () => {
    const { isConfirmed } = await Swal.fire({ title: `Inativar ${pessoaEditando.nome}?`, icon: 'warning', showCancelButton: true, confirmButtonText: 'Inativar', cancelButtonText: 'Voltar' });
    if (!isConfirmed) return;
    const r = await apiFin(`/pessoas-folha/${pessoaEditando.id}`, { method: 'DELETE' });
    if (r.status === 'ok') { showToast('Pessoa inativada.', 'success'); setPessoaEditando(null); carregar(); }
    else showToast(r.mensagem || 'Erro ao inativar', 'error');
  };

  const criarLancamento = async () => {
    if (!form.pessoa_folha_id || !form.valor) { showToast('Selecione a pessoa e informe o valor', 'error'); return; }
    setSalvando(true);
    const competencia = form.vencimento.slice(0, 7);
    const r = await apiFin('/payables', { method: 'POST', body: JSON.stringify({ tipo: 'FOLHA', descricao: `Folha — ${pessoas.find((p) => p.id === form.pessoa_folha_id)?.nome}`, pessoa_folha_id: form.pessoa_folha_id, valor_base: form.valor, bonus: form.bonus, descontos: form.descontos, competencia, vencimento: form.vencimento }) });
    setSalvando(false);
    if (r.status === 'ok') { showToast('Lançamento criado!', 'success'); setShowLancamento(false); carregar(); }
    else showToast(r.mensagem || 'Erro', 'error');
  };

  const registrarPagamento = async (payableId, valor) => {
    const r = await apiFin(`/payables/${payableId}/pagamentos`, { method: 'POST', body: JSON.stringify({ valor, data_pagamento: hoje() }) });
    if (r.status === 'ok') { showToast('Pagamento registrado!', 'success'); carregar(); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-4)' }}>
      <Panel title="Pessoas" actions={<Button size="sm" icon={faPlus} onClick={() => setShowPessoa(true)}>Pessoa</Button>} flush>
        {pessoas === null ? <EmptyState title="Carregando..." /> : pessoas.length === 0 ? (
          <EmptyState title="Nenhuma pessoa cadastrada" actions={<Button variant="primary" icon={faPlus} onClick={() => setShowPessoa(true)}>Pessoa</Button>} />
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead><tr><th>Nome</th><th>Função</th><th>Tipo</th><th>Status</th></tr></thead>
              <tbody>
                {pessoas.map((p) => (
                  <tr key={p.id} className="clickable" onClick={() => abrirEdicaoPessoa(p)}>
                    <td className="crm-table-primary">{p.nome}</td><td>{p.funcao || '—'}</td><td>{p.tipo}</td><td><StatusBadge status={p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)', flexWrap: 'wrap' }}>
        <span className="crm-form-hint">Competência:</span>
        <input className="crm-input" type="month" value={competencia} onChange={(e) => setCompetencia(e.target.value)} style={{ maxWidth: '160px' }} />
        {competencia && <Button size="sm" variant="ghost" onClick={() => setCompetencia('')}>Limpar</Button>}
        {!competencia && <span className="crm-form-hint">(mostrando todas)</span>}
        <span className="crm-form-hint" style={{ marginLeft: 'var(--crm-space-3)' }}>Colaborador:</span>
        <select className="crm-select" value={pessoaFiltro} onChange={(e) => setPessoaFiltro(e.target.value)} style={{ maxWidth: '220px' }}>
          <option value="">Todos</option>
          {(pessoas || []).map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
        </select>
        {pessoaFiltro && <Button size="sm" variant="ghost" onClick={() => setPessoaFiltro('')}>Limpar</Button>}
      </div>

      <Panel title="Lançamentos de folha" actions={<Button size="sm" icon={faPlus} onClick={() => setShowLancamento(true)}>Novo lançamento</Button>} flush>
        {lancamentos === null ? <EmptyState title="Carregando..." /> : lancamentos.length === 0 ? (
          <EmptyState title="Nenhum lançamento de folha ainda" />
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead><tr><th>Pessoa</th><th>Competência</th><th>Vencimento</th><th>Status</th><th>Valor</th><th /></tr></thead>
              <tbody>
                {lancamentos.map((l) => (
                  <tr key={l.id} className="clickable" onClick={() => setItemSelecionado({ ...l, _origem: 'payable' })}>
                    <td className="crm-table-primary">{l.pessoa_folha_nome || l.descricao}</td>
                    <td>{l.competencia}</td>
                    <td>{fmtDate(l.vencimento)}</td>
                    <td><StatusBadge status={l.situacao} /></td>
                    <td>{fmtBRL(l.valor)}</td>
                    <td className="crm-table-actions-cell" onClick={(e) => e.stopPropagation()}>
                      {l.situacao !== 'PAGO' && l.situacao !== 'CANCELADO' && <Button size="sm" variant="ghost" onClick={() => registrarPagamento(l.id, l.valor)}>Pagar</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Sheet open={showPessoa} onClose={() => setShowPessoa(false)} title="Cadastrar pessoa" footer={<Button variant="primary" onClick={criarPessoa}>Salvar</Button>}>
        <div className="crm-form-field"><label className="crm-form-label">Nome</label><input className="crm-input" value={novaPessoa.nome} onChange={(e) => setNovaPessoa({ ...novaPessoa, nome: e.target.value })} /></div>
        <div className="crm-form-field"><label className="crm-form-label">Função</label><input className="crm-input" value={novaPessoa.funcao} onChange={(e) => setNovaPessoa({ ...novaPessoa, funcao: e.target.value })} /></div>
        <div className="crm-form-field">
          <label className="crm-form-label">Tipo</label>
          <select className="crm-select" value={novaPessoa.tipo} onChange={(e) => setNovaPessoa({ ...novaPessoa, tipo: e.target.value })}>
            <option value="Sócio">Sócio</option><option value="Colaborador">Colaborador</option><option value="Freelancer">Freelancer</option>
          </select>
        </div>
      </Sheet>

      <Sheet open={showLancamento} onClose={() => setShowLancamento(false)} title="Novo lançamento de folha" footer={<Button variant="primary" loading={salvando} onClick={criarLancamento}>Salvar</Button>}>
        <div className="crm-form-field">
          <label className="crm-form-label">Pessoa</label>
          <select className="crm-select" value={form.pessoa_folha_id} onChange={(e) => setForm({ ...form, pessoa_folha_id: e.target.value })}>
            <option value="">Selecione...</option>
            {(pessoas || []).map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
        </div>
        <div className="crm-form-field"><label className="crm-form-label">Base</label><input className="crm-input" type="number" step="0.01" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} /></div>
        <div className="crm-form-field"><label className="crm-form-label">Bônus</label><input className="crm-input" type="number" step="0.01" value={form.bonus} onChange={(e) => setForm({ ...form, bonus: e.target.value })} /></div>
        <div className="crm-form-field"><label className="crm-form-label">Descontos</label><input className="crm-input" type="number" step="0.01" value={form.descontos} onChange={(e) => setForm({ ...form, descontos: e.target.value })} /></div>
        <div className="crm-form-field"><label className="crm-form-label">Data prevista</label><input className="crm-input" type="date" value={form.vencimento} onChange={(e) => setForm({ ...form, vencimento: e.target.value })} /></div>
      </Sheet>

      {pessoaEditando && (
        <Sheet
          open onClose={() => setPessoaEditando(null)} title={pessoaEditando.nome}
          footer={<Button variant="primary" loading={salvandoPessoa} onClick={salvarPessoa}>Salvar</Button>}
        >
          <div className="crm-form-field"><label className="crm-form-label">Nome</label><input className="crm-input" value={formPessoa.nome || ''} onChange={(e) => setFormPessoa({ ...formPessoa, nome: e.target.value })} /></div>
          <div className="crm-form-field"><label className="crm-form-label">Função</label><input className="crm-input" value={formPessoa.funcao || ''} onChange={(e) => setFormPessoa({ ...formPessoa, funcao: e.target.value })} /></div>
          <div className="crm-form-field">
            <label className="crm-form-label">Tipo</label>
            <select className="crm-select" value={formPessoa.tipo || ''} onChange={(e) => setFormPessoa({ ...formPessoa, tipo: e.target.value })}>
              <option value="Sócio">Sócio</option><option value="Colaborador">Colaborador</option><option value="Freelancer">Freelancer</option>
            </select>
          </div>
          <div className="crm-form-field">
            <label className="crm-form-label">Status</label>
            <select className="crm-select" value={formPessoa.status || ''} onChange={(e) => setFormPessoa({ ...formPessoa, status: e.target.value })}>
              <option value="ativo">Ativo</option><option value="inativo">Inativo</option>
            </select>
          </div>
          {formPessoa.status !== 'inativo' && (
            <Button variant="danger" onClick={inativarPessoa}>Inativar pessoa</Button>
          )}
        </Sheet>
      )}

      {itemSelecionado && (
        <LancamentoSheet item={itemSelecionado} onClose={() => setItemSelecionado(null)} onChanged={carregar} showToast={showToast} onNavegar={onNavegar} onAbrirAssinatura={() => {}} />
      )}
    </div>
  );
}

// ─── Relatórios (§26) ────────────────────────────────────────────────────
const anoAtual = () => String(new Date().getFullYear());

function AbaRelatorios({ showToast }) {
  const [periodo, setPeriodo] = useState({ de: mesAtual(), ate: mesAtual() });
  const [clienteId, setClienteId] = useState('');
  const [projetoId, setProjetoId] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [clientes, setClientes] = useState([]);
  const [projetos, setProjetos] = useState([]);
  const [dash, setDash] = useState(null);
  const [receivables, setReceivables] = useState(null);
  const [payables, setPayables] = useState(null);

  useEffect(() => {
    apiClientesFn('').then((r) => setClientes(r.lista || []));
    apiJson('/projetos').then((r) => { if (r.status === 'ok') setProjetos(r.lista || []); });
  }, []);

  useEffect(() => {
    const paramsDash = new URLSearchParams({ de: periodo.de, ate: periodo.ate });
    if (clienteId) paramsDash.set('cliente_id', clienteId);
    if (projetoId) paramsDash.set('projeto_id', projetoId);
    if (tipoFiltro) paramsDash.set('tipo', tipoFiltro);
    apiFin(`/dashboard/executivo?${paramsDash}`).then((r) => { if (r.status === 'ok') setDash(r.dashboard); });
    const params = new URLSearchParams({ competencia_de: periodo.de, competencia_ate: periodo.ate });
    if (clienteId) params.set('cliente_id', clienteId);
    if (projetoId) params.set('projeto_id', projetoId);
    apiFin(`/receivables?${params}`).then((r) => setReceivables((r.lista || []).filter((x) => !tipoFiltro || x.tipo === tipoFiltro)));
    apiFin(`/payables?${params}`).then((r) => setPayables(r.lista || []));
  }, [periodo, clienteId, projetoId, tipoFiltro]);

  const carregando = receivables === null || payables === null || !dash;

  const relatorios = useMemo(() => {
    if (carregando) return null;
    const porCliente = {};
    receivables.forEach((r) => {
      const k = r.cliente_nome || 'Sem cliente';
      porCliente[k] = (porCliente[k] || 0) + parseFloat(r.valor_pago || 0);
    });
    return {
      receitaPaga: parseFloat(dash.resultado_acumulado.receita_recebida),
      despesaPaga: parseFloat(dash.resultado_acumulado.despesas_pagas),
      resultado: parseFloat(dash.resultado_acumulado.resultado),
      mrr: parseFloat(dash.kpis.mrr_atual),
      inadimplencia: parseFloat(dash.inadimplencia.valor_vencido),
      recorrente: parseFloat(dash.composicao_receita.recorrente),
      avulsa: parseFloat(dash.composicao_receita.avulsa),
      porCliente,
    };
  }, [carregando, receivables, dash]);

  if (carregando) return <div className="crm-form-hint"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>;

  const cards = [
    { label: 'Receita (recebida)', valor: fmtBRL(relatorios.receitaPaga) },
    { label: 'Despesa (paga)', valor: fmtBRL(relatorios.despesaPaga) },
    { label: 'Resultado', valor: fmtBRL(relatorios.resultado), tone: relatorios.resultado < 0 ? 'danger' : undefined },
    { label: 'MRR atual', valor: fmtBRL(relatorios.mrr) },
    { label: 'Vencido no período', valor: fmtBRL(relatorios.inadimplencia), tone: relatorios.inadimplencia > 0 ? 'danger' : undefined },
    { label: 'Recorrente x avulsa', valor: `${fmtBRL(relatorios.recorrente)} / ${fmtBRL(relatorios.avulsa)}` },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-4)', flexWrap: 'wrap' }}>
        <PeriodFilterBar onChange={(de, ate) => setPeriodo({ de, ate })} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)' }}>
          <span className="crm-form-hint">Cliente:</span>
          <select className="crm-select" value={clienteId} onChange={(e) => setClienteId(e.target.value)} style={{ maxWidth: '180px' }}>
            <option value="">Todos</option>
            {clientes.map((c) => <option key={c._id} value={c._id}>{c.nome}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)' }}>
          <span className="crm-form-hint">Projeto:</span>
          <select className="crm-select" value={projetoId} onChange={(e) => setProjetoId(e.target.value)} style={{ maxWidth: '180px' }}>
            <option value="">Todos</option>
            {projetos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)' }}>
          <span className="crm-form-hint">Tipo:</span>
          <select className="crm-select" value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)} style={{ maxWidth: '140px' }}>
            <option value="">Todos</option>
            <option value="ASSINATURA">Recorrente</option>
            <option value="AVULSO">Avulsa</option>
          </select>
        </label>
      </div>

      <KpiGrid>
        {cards.map((c) => (
          <div key={c.label}>
            <KpiCard label={c.label} value={c.valor} tone={c.tone} />
            {c.sub && <div className="crm-form-hint" style={{ marginTop: 2 }}>{c.sub}</div>}
          </div>
        ))}
      </KpiGrid>

      <Panel title="Por cliente" actions={<Button size="sm" icon={faDownload} onClick={() => exportarCSV('receita_por_cliente.csv', [['Cliente', 'Valor'], ...Object.entries(relatorios.porCliente)])}>CSV</Button>} flush>
        {Object.keys(relatorios.porCliente).length === 0 ? <EmptyState title="Nenhuma receita neste período" /> : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead><tr><th>Cliente</th><th>Total</th></tr></thead>
              <tbody>{Object.entries(relatorios.porCliente).map(([k, v]) => <tr key={k}><td className="crm-table-primary">{k}</td><td>{fmtBRL(v)}</td></tr>)}</tbody>
            </table>
          </div>
        )}
      </Panel>

      <Button
        variant="outline"
        icon={faDownload}
        onClick={() => exportarCSV('financeiro_periodo.csv', [
          ['Tipo', 'Descrição', 'Cliente/Fornecedor', 'Vencimento', 'Status', 'Valor'],
          ...receivables.map((r) => ['Receita', r.descricao, r.cliente_nome || '', r.vencimento, r.situacao, r.valor]),
          ...payables.map((p) => [p.tipo === 'FOLHA' ? 'Folha' : 'Despesa', p.descricao, p.fornecedor_nome || p.pessoa_folha_nome || '', p.vencimento, p.situacao, p.valor]),
        ])}
      >
        Exportar período (CSV)
      </Button>
    </div>
  );
}

// ─── WhatsApp / WAHA (§23) ───────────────────────────────────────────────
const STATUS_SESSAO_TONE = { WORKING: 'positive', STARTING: 'warning', SCAN_QR_CODE: 'warning', QR_READY: 'warning', FAILED: 'danger', STOPPED: 'neutral' };

// Estados por onde a sessão SÓ PASSA — o WhatsApp leva alguns segundos entre
// "iniciando" e "pronto para ler o QR". Antes a tela lia o status uma única
// vez logo após "Iniciar sessão", congelava em STARTING, e o pedido de QR
// (feito cedo demais) voltava erro — o usuário via "Falha" para algo que se
// resolve sozinho. Enquanto o status estiver aqui, a tela repergunta.
const STATUS_TRANSITORIOS = ['STARTING', 'INICIANDO'];
// Estados em que o QR já existe e pode ser buscado. Dois nomes porque
// gateways diferentes rotulam o mesmo momento de formas distintas — a
// lista fica assim para não quebrar se o provider mudar.
const STATUS_COM_QR = ['SCAN_QR_CODE', 'QR_READY'];

const PROVIDER_LABEL = { waha: 'WAHA' };

function AbaWhatsApp({ showToast }) {
  const [config, setConfig] = useState(null);
  const [sessao, setSessao] = useState(null);
  const [qrcode, setQrcode] = useState(null);
  const [carregandoQr, setCarregandoQr] = useState(false);
  const [erro, setErro] = useState(null); // { mensagem, tentarNovamente } — persistente, não some sozinho como o toast
  const [telefoneAlertas, setTelefoneAlertas] = useState('');
  const [salvandoTelefone, setSalvandoTelefone] = useState(false);
  const [salvandoProvider, setSalvandoProvider] = useState(false);
  const [testeTelefone, setTesteTelefone] = useState('');
  const [testeMensagem, setTesteMensagem] = useState('');
  const [enviandoTeste, setEnviandoTeste] = useState(false);

  const carregar = useCallback(() => {
    apiFin('/waha/configuracao').then((r) => { if (r.status === 'ok') { setConfig(r); setTelefoneAlertas(r.telefone_alertas || ''); } });
    apiFin('/waha/status_sessao').then((r) => { if (r.status === 'ok') setSessao(r.sessao); });
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  // Acompanha a sessão enquanto ela está saindo de "iniciando". Sem isto a
  // tela mostrava STARTING para sempre — o backend já tinha evoluído para
  // SCAN_QR_CODE segundos depois, e ninguém perguntava de novo.
  // Para de perguntar assim que o estado se resolve (QR pronto, conectado ou
  // falha), para não ficar batendo no servidor à toa.
  useEffect(() => {
    if (!sessao || sessao.conectado) return undefined;
    if (!STATUS_TRANSITORIOS.includes(sessao.status_sessao)) return undefined;
    const timer = setInterval(() => {
      apiFin('/waha/status_sessao').then((r) => { if (r.status === 'ok') setSessao(r.sessao); });
    }, 3000);
    // Teto de segurança: se em 90s a sessão não saiu de "iniciando", algo
    // está errado de verdade e o poll silencioso viraria espera infinita.
    const limite = setTimeout(() => clearInterval(timer), 90000);
    return () => { clearInterval(timer); clearTimeout(limite); };
  }, [sessao]);

  const salvarTelefoneAlertas = async () => {
    setSalvandoTelefone(true);
    const r = await apiFin('/waha/configuracao', { method: 'POST', body: JSON.stringify({ telefone_alertas: telefoneAlertas || null }) });
    setSalvandoTelefone(false);
    if (r.status === 'ok') showToast('Telefone de alertas salvo.', 'success');
    else showToast(r.mensagem || 'Erro ao salvar', 'error');
  };

  const alternarHabilitado = async () => {
    const r = await apiFin('/waha/configuracao', { method: 'POST', body: JSON.stringify({ WAHA_ENABLED: !config.valores.WAHA_ENABLED }) });
    if (r.status === 'ok') { showToast('Configuração salva.', 'success'); carregar(); }
  };

  const alternarProvider = async (novoProvider) => {
    if (novoProvider === config.valores.whatsapp_provider) return;
    setSalvandoProvider(true);
    setQrcode(null);
    const r = await apiFin('/waha/configuracao', { method: 'POST', body: JSON.stringify({ whatsapp_provider: novoProvider }) });
    setSalvandoProvider(false);
    if (r.status === 'ok') { showToast(`Provider alterado para ${PROVIDER_LABEL[novoProvider] || novoProvider}.`, 'success'); carregar(); }
    else showToast(r.mensagem || 'Erro ao trocar provider', 'error');
  };

  const enviarTeste = async () => {
    if (!testeTelefone.trim() || !testeMensagem.trim()) { showToast('Informe telefone e mensagem.', 'error'); return; }
    setEnviandoTeste(true);
    const r = await apiFin('/waha/enviar_teste', { method: 'POST', body: JSON.stringify({ telefone: testeTelefone, mensagem: testeMensagem }) });
    setEnviandoTeste(false);
    if (r.status === 'ok') showToast('Mensagem de teste enviada.', 'success');
    else showToast(r.resposta?.detalhe || r.mensagem || 'Falha ao enviar mensagem de teste', 'error');
  };

  const iniciarSessao = async () => {
    setErro(null);
    setQrcode(null);
    const r = await apiFin('/waha/iniciar_sessao', { method: 'POST' });
    if (r.status === 'ok') {
      showToast('Sessão iniciando — o QR aparece em alguns segundos.', 'success');
      carregar();  // o poll acima assume daqui até o QR ficar pronto
    } else setErro({ mensagem: r.mensagem || 'Erro ao iniciar sessão do WhatsApp', tentarNovamente: iniciarSessao });
  };

  const pararSessao = async () => {
    setErro(null);
    const r = await apiFin('/waha/parar_sessao', { method: 'POST' });
    if (r.status === 'ok') { showToast('Sessão desconectada.', 'success'); carregar(); }
    else setErro({ mensagem: r.mensagem || 'Erro ao desconectar sessão', tentarNovamente: pararSessao });
  };

  const buscarQr = useCallback(async () => {
    setErro(null);
    setCarregandoQr(true);
    const r = await apiFin('/waha/qrcode');
    setCarregandoQr(false);
    if (r.status === 'ok') { setQrcode(r.qrcode); return; }
    // `iniciando: true` significa "ainda não", não "deu errado". Tratar isso
    // como falha era o que punha um banner vermelho na tela por causa de uma
    // espera normal de poucos segundos.
    if (r.iniciando) { showToast('WhatsApp ainda inicializando — o QR aparece em instantes.', 'info'); return; }
    setErro({ mensagem: r.mensagem || 'Erro ao obter QR code', tentarNovamente: buscarQr });
    // `showToast` nas dependências: sem ela o callback congelaria a versão
    // inicial da função e, se o pai a recriasse, o aviso de "ainda
    // inicializando" iria para um toast que não existe mais.
  }, [showToast]);

  // Assim que a sessão chega ao ponto de ler o QR, busca sozinho: chegar até
  // aqui e ainda ter que clicar "Ver QR Code" é um passo sem propósito.
  useEffect(() => {
    if (sessao && !sessao.conectado && STATUS_COM_QR.includes(sessao.status_sessao) && !qrcode && !carregandoQr) {
      buscarQr();
    }
  }, [sessao, qrcode, carregandoQr, buscarQr]);

  if (!config || !sessao) return <div className="crm-form-hint"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>;

  const dryRunAtivo = !config.valores.WAHA_ENABLED;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-4)' }}>
      {dryRunAtivo && (
        <div className="crm-panel" style={{ padding: 'var(--crm-space-3) var(--crm-space-4)', borderColor: 'var(--crm-warning, #d97706)' }}>
          <Badge tone="warning">Dry Run ativo</Badge>
          <span className="crm-form-hint" style={{ marginLeft: 'var(--crm-space-2)' }}>Cobranças e mensagens automáticas são simuladas — nada é enviado de verdade até ativar o envio automático abaixo.</span>
        </div>
      )}

      {erro && (
        <div className="crm-panel" style={{ padding: 'var(--crm-space-3) var(--crm-space-4)', borderColor: 'var(--crm-danger)', display: 'flex', alignItems: 'center', gap: 'var(--crm-space-3)', flexWrap: 'wrap' }}>
          <Badge tone="danger">Falha</Badge>
          <span style={{ flex: 1 }}>{erro.mensagem}</span>
          <Button size="sm" variant="outline" icon={faSync} onClick={erro.tentarNovamente}>Tentar novamente</Button>
        </div>
      )}

      <Panel title="WhatsApp desta empresa">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)' }}>
            <Badge tone={sessao.conectado ? 'positive' : (STATUS_SESSAO_TONE[sessao.status_sessao] || 'neutral')}>
              {sessao.conectado ? 'Conectado' : sessao.status_sessao || 'Desconectado'}
            </Badge>
            <Button size="sm" variant="ghost" icon={faSync} onClick={carregar}>Atualizar</Button>
          </div>
          <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
            <Button size="sm" variant="outline" icon={faPlay} onClick={iniciarSessao}>Iniciar sessão</Button>
            <Button size="sm" variant="outline" icon={faQrcode} loading={carregandoQr} onClick={buscarQr}>Ver QR Code</Button>
            {sessao.conectado && <Button size="sm" variant="danger" icon={faStop} onClick={pararSessao}>Desconectar</Button>}
          </div>
          {qrcode && (
            <div style={{ textAlign: 'center' }}>
              <img src={qrcode} alt="QR Code WhatsApp" style={{ maxWidth: '220px', borderRadius: 'var(--crm-radius-md)' }} />
            </div>
          )}
        </div>
      </Panel>

      <Panel title="Configurações">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-3)' }}>
          {Array.isArray(config.providers_disponiveis) && config.providers_disponiveis.length > 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-1)' }}>
              <label style={{ fontSize: 'var(--crm-text-sm)', fontWeight: 600 }}>Provider de WhatsApp</label>
              <select
                className="crm-input"
                style={{ maxWidth: '280px' }}
                value={config.valores.whatsapp_provider || 'waha'}
                disabled={salvandoProvider}
                onChange={(e) => alternarProvider(e.target.value)}
              >
                {config.providers_disponiveis.map((p) => (
                  <option key={p} value={p}>{PROVIDER_LABEL[p] || p}</option>
                ))}
              </select>
              <div className="crm-form-hint">Trocar de provider cria/usa uma sessão própria — a conexão do provider anterior não é migrada automaticamente. Reconecte (Ver QR Code) depois de trocar.</div>
            </div>
          )}


          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)' }}>
            <input type="checkbox" checked={!!config.valores.WAHA_ENABLED} onChange={alternarHabilitado} />
            Envio automático {config.valores.WAHA_ENABLED ? 'ATIVADO' : 'DESATIVADO (modo Dry Run)'}
          </label>
          <div className="crm-form-hint">Sessão: {config.configuracao.session || '—'}</div>
          <div className="crm-form-hint">Servidor: {config.configuracao.base_url}</div>
          {config.configuracao.country_code && <div className="crm-form-hint">Código país: {config.configuracao.country_code}</div>}
          <div className="crm-form-hint">API autenticada: {config.configuracao.tem_api_key ? 'sim' : 'não'}</div>
        </div>
      </Panel>

      <Panel title="Enviar teste">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-3)' }}>
          <div className="crm-form-hint">Manda uma mensagem avulsa agora mesmo pelo provider ativo — útil pra confirmar que a sessão está de fato conectada, sem esperar o próximo envio automático.</div>
          <div style={{ display: 'flex', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
            <input className="crm-input" placeholder="Telefone (ex: 11999999999)" value={testeTelefone} onChange={(e) => setTesteTelefone(e.target.value)} style={{ maxWidth: '220px' }} />
            <input className="crm-input" placeholder="Mensagem" value={testeMensagem} onChange={(e) => setTesteMensagem(e.target.value)} style={{ flex: 1, minWidth: '220px' }} />
            <Button variant="primary" size="sm" icon={faPaperPlane} loading={enviandoTeste} onClick={enviarTeste}>Enviar teste</Button>
          </div>
        </div>
      </Panel>

      <Panel title="Alertas automáticos para o dono">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-3)' }}>
          <div className="crm-form-hint">
            Todo dia, se houver algo pendente, o Lockus manda um resumo por WhatsApp pra este número:
            despesas vencendo, estoque abaixo do mínimo e tarefas atrasadas. Deixe em branco para desativar.
          </div>
          <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
            <input className="crm-input" placeholder="Ex: 11999999999" value={telefoneAlertas} onChange={(e) => setTelefoneAlertas(e.target.value)} style={{ maxWidth: '220px' }} />
            <Button variant="primary" size="sm" loading={salvandoTelefone} onClick={salvarTelefoneAlertas}>Salvar</Button>
          </div>
        </div>
      </Panel>
    </div>
  );
}

// ─── Root ────────────────────────────────────────────────────────────────
const Financeiro = ({ showToast, clienteIdContexto, abrirCriacaoTipo, onNavegar }) => {
  const [aba, setAba] = useState(abrirCriacaoTipo === 'ASSINATURA' ? 'assinaturas' : abrirCriacaoTipo === 'RECEITA' ? 'receitas' : 'geral');
  const [assinaturaAberta, setAssinaturaAberta] = useState(null);
  // Item 20 do pedido: dashboard não pode virar gráfico morto — todo KPI/
  // gráfico que representa registros reais precisa levar até eles. Como as
  // abas vivem dentro do mesmo componente (não são rotas), o drill-down é
  // "trocar de aba + já chegar com o filtro certo aplicado".
  const [filtroReceitas, setFiltroReceitas] = useState(null);
  const [filtroCobrancas, setFiltroCobrancas] = useState(null);
  const [filtroAssinaturas, setFiltroAssinaturas] = useState(null);
  const [filtroDespesas, setFiltroDespesas] = useState(null);

  const irParaReceitas = (filtro) => { setFiltroReceitas(filtro); setAba('receitas'); };
  const irParaCobrancas = (status) => { setFiltroCobrancas(status); setAba('cobrancas'); };
  const irParaAssinaturas = (filtro) => { setFiltroAssinaturas(filtro); setAba('assinaturas'); };
  const irParaDespesas = (filtro) => { setFiltroDespesas(filtro); setAba('despesas'); };

  return (
    <div>
      <PageHeader
        icon={faWallet}
        title="Financeiro"
        subtitle="Receitas, despesas, assinaturas e cobranças em um só lugar."
      />
      <Tabs tabs={TABS} atual={aba} onChange={setAba} />
      <div style={{ marginTop: 'var(--crm-space-4)' }}>
        {aba === 'geral' && (
          <AbaVisaoGeral
            showToast={showToast}
            onNavegar={onNavegar}
            onIrParaReceitas={irParaReceitas}
            onIrParaCobrancas={irParaCobrancas}
            onIrParaAssinaturas={irParaAssinaturas}
            onIrParaDespesas={irParaDespesas}
            onIrParaAba={setAba}
          />
        )}
        {aba === 'receitas' && <AbaReceitas showToast={showToast} clienteIdContexto={clienteIdContexto} abrirCriacao={abrirCriacaoTipo === 'RECEITA'} onNavegar={onNavegar} onAbrirAssinatura={setAssinaturaAberta} filtroInicial={filtroReceitas} />}
        {aba === 'assinaturas' && <AbaAssinaturas showToast={showToast} clienteIdContexto={clienteIdContexto} abrirCriacao={abrirCriacaoTipo === 'ASSINATURA'} onAbrirAssinatura={setAssinaturaAberta} filtroInicial={filtroAssinaturas} onNavegar={onNavegar} />}
        {aba === 'cobrancas' && <AbaCobrancas showToast={showToast} onNavegar={onNavegar} onAbrirAssinatura={setAssinaturaAberta} filtroInicial={filtroCobrancas} />}
        {aba === 'despesas' && <AbaDespesas showToast={showToast} onNavegar={onNavegar} filtroInicial={filtroDespesas} />}
        {aba === 'folha' && <AbaFolha showToast={showToast} onNavegar={onNavegar} />}
        {aba === 'relatorios' && <AbaRelatorios showToast={showToast} />}
        {aba === 'whatsapp' && <AbaWhatsApp showToast={showToast} />}
      </div>
      <AssinaturaSheet assinaturaId={assinaturaAberta} open={!!assinaturaAberta} onClose={() => setAssinaturaAberta(null)} onChanged={() => {}} showToast={showToast} onNavegar={onNavegar} />
    </div>
  );
};

export default Financeiro;
